import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { useParams } from 'react-router-dom';
import { AiOutlineHeart, AiFillHeart, AiOutlineShoppingCart } from 'react-icons/ai';
import { BsStarFill, BsStarHalf, BsStar } from 'react-icons/bs';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '../Header/Header';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { addToCart } from '../../redux/slices/cartSlice';
import { globalLogout } from '../../Utils/Logout';
import Footer from '../../Components/Footer/Footer';
import { 
  fetchWishlist,
  addItemToWishlist,
  removeItemFromWishlist
} from '../../redux/thunks/wishlistThunks';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  },
  hover: {
    y: -5,
    scale: 1.02,
    transition: {
      duration: 0.2
    }
  }
};

const buttonVariants = {
  hover: {
    scale: 1.05,
    boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.1)",
    transition: {
      duration: 0.2
    }
  },
  tap: {
    scale: 0.95
  }
};

const ProductsByCategory = () => {
  const { categoryId } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [category, setCategory] = useState(null);
  const [productRatings, setProductRatings] = useState({});
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const token = Cookies.get("ecom_user_token");
  const { items: wishlist, loading: wishlistLoading } = useSelector(state => state.wishlist);

  // Fetch initial wishlist when component mounts
  useEffect(() => {
    if (token) {
      dispatch(fetchWishlist());
    }
  }, [token, dispatch]);

  // Function to fetch ratings for a product
  const fetchProductRatings = async (productId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/rating/productRating/${productId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      const ratings = response.data.ratings || [];
      return ratings;
    } catch (error) {
        globalLogout(error)
    }
  };

  // Calculate average rating for a product
  const calculateAverageRating = (ratings) => {
    if (ratings.length === 0) return 0;
    const sum = ratings.reduce((acc, review) => acc + review.ratings, 0);
    return sum / ratings.length;
  };

  const handleAddToCart = async (product, e) => {
    e.stopPropagation();
    if (product.stock <= 0) {
      alert("This product is out of stock");
      return;
    }
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/buyer/add-to-cart`,
        {
          productId: product._id,
          quantity: 1
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      navigate('/cart');
    } catch (error) {
      // console.error("Add to cart API failed:", error);
      // alert("Failed to add to cart. Please try again.");
      globalLogout(error)
    }
  };

  const toggleWishlist = async (productId, e) => {
    e.stopPropagation();
    if (!token) {
      alert("Please login to manage your wishlist");
      navigate('/login');
      return;
    }

    try {
      if (wishlist.some(item => item.productId === productId)) {
        await dispatch(removeItemFromWishlist(productId));
      } else {
        await dispatch(addItemToWishlist(productId));
      }
    } catch (error) {
      console.error("Wishlist operation failed:", error);
      alert("Failed to update wishlist. Please try again.");
    }
  };

  const fetchProductDetails = (productId) => {
    navigate(`/product/${productId}`);
  };

  useEffect(() => {
    const fetchProducts = async () => {
      if (!categoryId) return;

      setLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/seller/product/get-by-category/${categoryId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        // Filter out inactive products
        const activeProducts = response.data.products.filter(
          product => product.status === 'approved'
        );
        setProducts(activeProducts || []);
        setCategory(response?.data?.products[0]?.category?.name);

        // Fetch ratings for each product
        const ratingsMap = {};
        for (const product of activeProducts) {
          const ratings = await fetchProductRatings(product._id);
          ratingsMap[product._id] = ratings;
        }
        setProductRatings(ratingsMap);
      } catch (err) {
        setError(err.message || "Something went wrong.");
        globalLogout(err)
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [categoryId, token]);

  const renderRatingStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    
    for (let i = 0; i < fullStars; i++) {
      stars.push(<BsStarFill key={`full-${i}`} className="text-yellow-400" />);
    }
    
    if (hasHalfStar) {
      stars.push(<BsStarHalf key="half" className="text-yellow-400" />);
    }
    
    const emptyStars = 5 - stars.length;
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<BsStar key={`empty-${i}`} className="text-yellow-400" />);
    }
    
    return stars;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <div className="flex-grow flex items-center justify-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="h-12 w-12 border-4 border-blue-500 border-t-transparent rounded-full"
          />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <div className="flex-grow flex items-center justify-center">
          <motion.div 
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white p-8 rounded-xl shadow-lg max-w-md text-center"
          >
            <h2 className="text-2xl font-bold text-red-500 mb-4">Error Loading Products</h2>
            <p className="text-gray-600">{error}</p>
            <motion.button
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
              onClick={() => window.location.reload()}
              className="mt-4 bg-blue-600 text-white px-6 py-2 rounded-lg font-medium"
            >
              Try Again
            </motion.button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      
      <main className="flex-grow container mx-auto px-4 py-8">
        {/* Category Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-12 text-center"
        >
          <h1 className="text-4xl font-bold text-gray-800 mb-2">
            {category || 'Our Products'}
          </h1>
          <div className="w-24 h-1 bg-blue-600 mx-auto rounded-full"></div>
        </motion.div>

        {/* Products Grid */}
        {products.length > 0 ? (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8"
          >
            {products.map((product) => {
              const ratings = productRatings[product._id] || [];
              const avgRating = calculateAverageRating(ratings);
              const reviewCount = ratings.length;
              const isInWishlist = wishlist.some(item => item.productId === product._id);

              return (
                <motion.div
                  key={product._id}
                  variants={itemVariants}
                  whileHover="hover"
                  onClick={() => fetchProductDetails(product._id)}
                  className={`bg-white rounded-xl shadow-md overflow-hidden cursor-pointer relative transition-all duration-300 ${
                    product.stock <= 0 ? 'opacity-70 grayscale' : ''
                  }`}
                >
                  {/* Product Image */}
                  <div className="relative h-64 overflow-hidden group">
                    <img
                      src={product.images[0]?.blob || 'https://via.placeholder.com/300'}
                      alt={product.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    
                    {/* Wishlist Button */}
                    <motion.button
                      onClick={(e) => toggleWishlist(product._id, e)}
                      disabled={wishlistLoading || product.stock <= 0}
                      className={`absolute top-3 right-3 p-2 rounded-full bg-white bg-opacity-80 backdrop-blur-sm shadow-md ${
                        product.stock <= 0 ? 'cursor-not-allowed' : ''
                      }`}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      {wishlistLoading ? (
                        <div className="h-5 w-5 flex items-center justify-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500"></div>
                        </div>
                      ) : isInWishlist ? (
                        <AiFillHeart className="text-red-500 h-5 w-5" />
                      ) : (
                        <AiOutlineHeart className="text-gray-600 h-5 w-5" />
                      )}
                    </motion.button>
                    
                    {/* Stock Status */}
                    {product.stock <= 0 && (
                      <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                        <span className="bg-white px-4 py-1 rounded-full text-sm font-bold text-gray-800">
                          Out of Stock
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Product Info */}
                  <div className="p-5">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="text-lg font-bold text-gray-800 line-clamp-1">
                        {product?.name}
                      </h3>
                      <span className="text-lg font-bold text-blue-600 whitespace-nowrap ml-2">
                        ₹{product?.price?.toFixed(2) || '0.00'}
                      </span>
                    </div>
                    
                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                      {product?.description}
                    </p>
                    
                    {/* Rating */}
                    <div className="flex items-center mb-4">
                      <div className="flex mr-2">
                        {renderRatingStars(avgRating)}
                      </div>
                      <span className="text-xs text-gray-500">
                        ({reviewCount} {reviewCount === 1 ? 'review' : 'reviews'})
                      </span>
                    </div>
                    
                    {/* Add to Cart Button */}
                    <motion.button
                      onClick={(e) => handleAddToCart(product, e)}
                      disabled={product?.stock <= 0}
                      variants={buttonVariants}
                      whileHover={product.stock > 0 ? "hover" : {}}
                      whileTap={product.stock > 0 ? "tap" : {}}
                      className={`w-full py-3 rounded-lg font-medium flex items-center justify-center space-x-2 ${
                        product.stock > 0 
                          ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white' 
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      <AiOutlineShoppingCart className="text-lg" />
                      <span>{product.stock > 0 ? 'Add to Cart' : 'Out of Stock'}</span>
                    </motion.button>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col items-center justify-center py-16"
          >
            <div className="bg-gray-200 p-8 rounded-full mb-6">
              <AiOutlineShoppingCart className="text-gray-400 text-5xl" />
            </div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              No Products Found
            </h3>
            <p className="text-gray-500 mb-6 text-center max-w-md">
              There are currently no active products in this category. Please check back later.
            </p>
            <motion.button
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
              onClick={() => navigate('/')}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium"
            >
              Continue Shopping
            </motion.button>
          </motion.div>
        )}
      </main>
      
   <Footer />
    </div>
  );
};

export default ProductsByCategory;

// import React, { useState, useEffect } from 'react';
// import axios from 'axios';
// import Cookies from 'js-cookie';
// import { useParams } from 'react-router-dom';
// import { AiOutlineHeart, AiFillHeart, AiOutlineShoppingCart } from 'react-icons/ai';
// import { BsStarFill, BsStarHalf, BsStar } from 'react-icons/bs';
// import { motion, AnimatePresence } from 'framer-motion';
// import Header from '../Header/Header';
// import { useDispatch, useSelector } from 'react-redux';
// import { useNavigate } from 'react-router-dom';
// import { addToCart } from '../../redux/slices/cartSlice';
// import { 
//   fetchWishlist,
//   addItemToWishlist,
//   removeItemFromWishlist
// } from '../../redux/thunks/wishlistThunks';

// // Animation variants
// const containerVariants = {
//   hidden: { opacity: 0 },
//   visible: {
//     opacity: 1,
//     transition: {
//       staggerChildren: 0.1,
//       delayChildren: 0.2
//     }
//   }
// };

// const itemVariants = {
//   hidden: { y: 20, opacity: 0 },
//   visible: {
//     y: 0,
//     opacity: 1,
//     transition: {
//       duration: 0.5,
//       ease: "easeOut"
//     }
//   },
//   hover: {
//     y: -5,
//     scale: 1.02,
//     transition: {
//       duration: 0.2
//     }
//   }
// };

// const buttonVariants = {
//   hover: {
//     scale: 1.05,
//     boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.1)",
//     transition: {
//       duration: 0.2
//     }
//   },
//   tap: {
//     scale: 0.95
//   }
// };

// const ProductsByCategory = () => {
//   const { categoryId } = useParams();
//   const [products, setProducts] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(null);
//   const [category, setCategory] = useState(null);
//   const [productRatings, setProductRatings] = useState({});
//   const dispatch = useDispatch();
//   const navigate = useNavigate();

//   const token = Cookies.get("ecom_user_token");
//   const { items: wishlist, loading: wishlistLoading } = useSelector(state => state.wishlist);

//   // Fetch initial wishlist when component mounts
//   useEffect(() => {
//     if (token) {
//       dispatch(fetchWishlist());
//     }
//   }, [token, dispatch]);

//   // Function to fetch ratings for a product
//   const fetchProductRatings = async (productId) => {
//     try {
//       const response = await axios.get(
//         `https://seller-backend-29o9.onrender.com/api/rating/productRating/${productId}`,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`
//           }
//         }
//       );
//       const ratings = response.data.ratings || [];
//       return ratings;
//     } catch (error) {
//       console.error("Failed to fetch ratings:", error);
//       return [];
//     }
//   };

//   // Calculate average rating for a product
//   const calculateAverageRating = (ratings) => {
//     if (ratings.length === 0) return 0;
//     const sum = ratings.reduce((acc, review) => acc + review.ratings, 0);
//     return sum / ratings.length;
//   };

//   const handleAddToCart = async (product, e) => {
//     e.stopPropagation();
//     if (product.stock <= 0) {
//       alert("This product is out of stock");
//       return;
//     }
    
//     try {
//       const response = await axios.post(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/add-to-cart`,
//         {
//           productId: product._id,
//           quantity: 1
//         },
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             'Content-Type': 'application/json'
//           }
//         }
//       );
//       navigate('/cart');
//     } catch (error) {
//       console.error("Add to cart API failed:", error);
//       alert("Failed to add to cart. Please try again.");
//     }
//   };

//   const toggleWishlist = async (productId, e) => {
//     e.stopPropagation();
//     if (!token) {
//       alert("Please login to manage your wishlist");
//       navigate('/login');
//       return;
//     }

//     try {
//       if (wishlist.some(item => item.productId === productId)) {
//         await dispatch(removeItemFromWishlist(productId));
//       } else {
//         await dispatch(addItemToWishlist(productId));
//       }
//     } catch (error) {
//       console.error("Wishlist operation failed:", error);
//       alert("Failed to update wishlist. Please try again.");
//     }
//   };

//   const fetchProductDetails = (productId) => {
//     navigate(`/product/${productId}`);
//   };

//   useEffect(() => {
//     const fetchProducts = async () => {
//       if (!categoryId) return;

//       setLoading(true);
//       try {
//         const response = await axios.get(
//           `${process.env.REACT_APP_BACKEND_URL}/api/seller/product/get-by-category/${categoryId}`,
//           {
//             headers: {
//               Authorization: `Bearer ${token}`
//             }
//           }
//         );
//         // Filter out inactive products
//         const activeProducts = response.data.products.filter(
//           product => product.status === 'approved'
//         );
//         setProducts(activeProducts || []);
//         setCategory(response?.data?.products[0]?.category?.name);

//         // Fetch ratings for each product
//         const ratingsMap = {};
//         for (const product of activeProducts) {
//           const ratings = await fetchProductRatings(product._id);
//           ratingsMap[product._id] = ratings;
//         }
//         setProductRatings(ratingsMap);
//       } catch (err) {
//         setError(err.message || "Something went wrong.");
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchProducts();
//   }, [categoryId, token]);

//   const renderRatingStars = (rating) => {
//     const stars = [];
//     const fullStars = Math.floor(rating);
//     const hasHalfStar = rating % 1 >= 0.5;
    
//     for (let i = 0; i < fullStars; i++) {
//       stars.push(<BsStarFill key={`full-${i}`} className="text-yellow-400" />);
//     }
    
//     if (hasHalfStar) {
//       stars.push(<BsStarHalf key="half" className="text-yellow-400" />);
//     }
    
//     const emptyStars = 5 - stars.length;
//     for (let i = 0; i < emptyStars; i++) {
//       stars.push(<BsStar key={`empty-${i}`} className="text-yellow-400" />);
//     }
    
//     return stars;
//   };

//   if (loading) {
//     return (
//       <div className="min-h-screen bg-gray-50 flex flex-col">
//         <Header />
//         <div className="flex-grow flex items-center justify-center">
//           <motion.div
//             animate={{ rotate: 360 }}
//             transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
//             className="h-12 w-12 border-4 border-blue-500 border-t-transparent rounded-full"
//           />
//         </div>
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="min-h-screen bg-gray-50 flex flex-col">
//         <Header />
//         <div className="flex-grow flex items-center justify-center">
//           <motion.div 
//             initial={{ scale: 0.8, opacity: 0 }}
//             animate={{ scale: 1, opacity: 1 }}
//             className="bg-white p-8 rounded-xl shadow-lg max-w-md text-center"
//           >
//             <h2 className="text-2xl font-bold text-red-500 mb-4">Error Loading Products</h2>
//             <p className="text-gray-600">{error}</p>
//             <motion.button
//               variants={buttonVariants}
//               whileHover="hover"
//               whileTap="tap"
//               onClick={() => window.location.reload()}
//               className="mt-4 bg-blue-600 text-white px-6 py-2 rounded-lg font-medium"
//             >
//               Try Again
//             </motion.button>
//           </motion.div>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="min-h-screen bg-gray-50 flex flex-col">
//       <Header />
      
//       <main className="flex-grow container mx-auto px-4 py-8">
//         {/* Category Header */}
//         <motion.div 
//           initial={{ opacity: 0, y: -20 }}
//           animate={{ opacity: 1, y: 0 }}
//           transition={{ duration: 0.5 }}
//           className="mb-12 text-center"
//         >
//           <h1 className="text-4xl font-bold text-gray-800 mb-2">
//             {category || 'Our Products'}
//           </h1>
//           <div className="w-24 h-1 bg-blue-600 mx-auto rounded-full"></div>
//         </motion.div>

//         {/* Products Grid */}
//         {products.length > 0 ? (
//           <motion.div
//             variants={containerVariants}
//             initial="hidden"
//             animate="visible"
//             className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8"
//           >
//             {products.map((product) => {
//               const ratings = productRatings[product._id] || [];
//               const avgRating = calculateAverageRating(ratings);
//               const reviewCount = ratings.length;
//               const isInWishlist = wishlist.some(item => item.productId === product._id);

//               return (
//                 <motion.div
//                   key={product._id}
//                   variants={itemVariants}
//                   whileHover="hover"
//                   onClick={() => fetchProductDetails(product._id)}
//                   className={`bg-white rounded-xl shadow-md overflow-hidden cursor-pointer relative transition-all duration-300 ${
//                     product.stock <= 0 ? 'opacity-70 grayscale' : ''
//                   }`}
//                 >
//                   {/* Product Image */}
//                   <div className="relative h-64 overflow-hidden group">
//                     <img
//                       src={product.images[0]?.blob || 'https://via.placeholder.com/300'}
//                       alt={product.name}
//                       className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
//                     />
                    
//                     {/* Wishlist Button */}
//                     <motion.button
//                       onClick={(e) => toggleWishlist(product._id, e)}
//                       disabled={wishlistLoading || product.stock <= 0}
//                       className={`absolute top-3 right-3 p-2 rounded-full bg-white bg-opacity-80 backdrop-blur-sm shadow-md ${
//                         product.stock <= 0 ? 'cursor-not-allowed' : ''
//                       }`}
//                       whileHover={{ scale: 1.1 }}
//                       whileTap={{ scale: 0.9 }}
//                     >
//                       {wishlistLoading ? (
//                         <div className="h-5 w-5 flex items-center justify-center">
//                           <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500"></div>
//                         </div>
//                       ) : isInWishlist ? (
//                         <AiFillHeart className="text-red-500 h-5 w-5" />
//                       ) : (
//                         <AiOutlineHeart className="text-gray-600 h-5 w-5" />
//                       )}
//                     </motion.button>
                    
//                     {/* Stock Status */}
//                     {product.stock <= 0 && (
//                       <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
//                         <span className="bg-white px-4 py-1 rounded-full text-sm font-bold text-gray-800">
//                           Out of Stock
//                         </span>
//                       </div>
//                     )}
//                   </div>
                  
//                   {/* Product Info */}
//                   <div className="p-5">
//                     <div className="flex justify-between items-start mb-2">
//                       <h3 className="text-lg font-bold text-gray-800 line-clamp-1">
//                         {product?.name}
//                       </h3>
//                       <span className="text-lg font-bold text-blue-600 whitespace-nowrap ml-2">
//                         ₹{product?.price?.toFixed(2) || '0.00'}
//                       </span>
//                     </div>
                    
//                     <p className="text-gray-600 text-sm mb-3 line-clamp-2">
//                       {product?.description}
//                     </p>
                    
//                     {/* Rating */}
//                     <div className="flex items-center mb-4">
//                       <div className="flex mr-2">
//                         {renderRatingStars(avgRating)}
//                       </div>
//                       <span className="text-xs text-gray-500">
//                         ({reviewCount} {reviewCount === 1 ? 'review' : 'reviews'})
//                       </span>
//                     </div>
                    
//                     {/* Add to Cart Button */}
//                     <motion.button
//                       onClick={(e) => handleAddToCart(product, e)}
//                       disabled={product?.stock <= 0}
//                       variants={buttonVariants}
//                       whileHover={product.stock > 0 ? "hover" : {}}
//                       whileTap={product.stock > 0 ? "tap" : {}}
//                       className={`w-full py-3 rounded-lg font-medium flex items-center justify-center space-x-2 ${
//                         product.stock > 0 
//                           ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white' 
//                           : 'bg-gray-300 text-gray-500 cursor-not-allowed'
//                       }`}
//                     >
//                       <AiOutlineShoppingCart className="text-lg" />
//                       <span>{product.stock > 0 ? 'Add to Cart' : 'Out of Stock'}</span>
//                     </motion.button>
//                   </div>
//                 </motion.div>
//               );
//             })}
//           </motion.div>
//         ) : (
//           <motion.div
//             initial={{ opacity: 0 }}
//             animate={{ opacity: 1 }}
//             className="flex flex-col items-center justify-center py-16"
//           >
//             <div className="bg-gray-200 p-8 rounded-full mb-6">
//               <AiOutlineShoppingCart className="text-gray-400 text-5xl" />
//             </div>
//             <h3 className="text-xl font-semibold text-gray-700 mb-2">
//               No Products Found
//             </h3>
//             <p className="text-gray-500 mb-6 text-center max-w-md">
//               There are currently no active products in this category. Please check back later.
//             </p>
//             <motion.button
//               variants={buttonVariants}
//               whileHover="hover"
//               whileTap="tap"
//               onClick={() => navigate('/')}
//               className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium"
//             >
//               Continue Shopping
//             </motion.button>
//           </motion.div>
//         )}
//       </main>
      
//       {/* Footer */}
//       <footer className="bg-gray-800 text-white py-8">
//         <div className="container mx-auto px-4 text-center">
//           <p>© 2023 E-Commerce Store. All rights reserved.</p>
//         </div>
//       </footer>
//     </div>
//   );
// };

// export default ProductsByCategory;

// import React, { useState, useEffect } from 'react';
// import axios from 'axios';
// import Cookies from 'js-cookie';
// import { useParams } from 'react-router-dom';
// import { AiOutlineHeart, AiFillHeart, AiOutlineClose, AiOutlineShoppingCart } from 'react-icons/ai';
// import { BsStarFill, BsStarHalf, BsStar } from 'react-icons/bs';
// import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
// import Header from '../Header/Header';
// import { useDispatch, useSelector } from 'react-redux';
// import { useNavigate } from 'react-router-dom';
// import { addToCart } from '../../redux/slices/cartSlice';
// import { 
//   fetchWishlist,
//   addItemToWishlist,
//   removeItemFromWishlist
// } from '../../redux/thunks/wishlistThunks';

// const ProductsByCategory = () => {
//   const { categoryId } = useParams();
//   const [products, setProducts] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(null);
//   const [category, setCategory] = useState(null);
//   const [selectedProduct, setSelectedProduct] = useState(null);
//   const [productDetails, setProductDetails] = useState(null);
//   const [productLoading, setProductLoading] = useState(false);
//   const [currentImageIndex, setCurrentImageIndex] = useState(0);
//   const [quantity, setQuantity] = useState(1);
//   const [productRatings, setProductRatings] = useState({});
//   const dispatch = useDispatch();
//   const navigate = useNavigate();

//   const token = Cookies.get("ecom_user_token");
//   const { items: wishlist, loading: wishlistLoading } = useSelector(state => state.wishlist);

//   // Fetch initial wishlist when component mounts
//   useEffect(() => {
//     if (token) {
//       dispatch(fetchWishlist());
//     }
//   }, [token, dispatch]);

//   // Function to fetch ratings for a product
//   const fetchProductRatings = async (productId) => {
//     try {
//       const response = await axios.get(
//         `https://seller-backend-29o9.onrender.com/api/rating/productRating/${productId}`,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`
//           }
//         }
//       );
//       const ratings = response.data.ratings || [];
//       return ratings;
//     } catch (error) {
//       console.error("Failed to fetch ratings:", error);
//       return [];
//     }
//   };

//   // Calculate average rating for a product
//   const calculateAverageRating = (ratings) => {
//     if (ratings.length === 0) return 0;
//     const sum = ratings.reduce((acc, review) => acc + review.ratings, 0);
//     return sum / ratings.length;
//   };

//   const handleAddToCart = async (product) => {
//     if (product.stock <= 0) {
//       alert("This product is out of stock");
//       return;
//     }
    
//     try {
//       const response = await axios.post(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/add-to-cart`,
//         {
//           productId: product._id,
//           quantity: quantity
//         },
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             'Content-Type': 'application/json'
//           }
//         }
//       );
//       navigate('/cart');
//     } catch (error) {
//       console.error("Add to cart API failed:", error);
//       alert("Failed to add to cart. Please try again.");
//     }
//   };

//   const toggleWishlist = async (productId) => {
//     if (!token) {
//       alert("Please login to manage your wishlist");
//       navigate('/login');
//       return;
//     }

//     try {
//       if (wishlist.some(item => item.productId === productId)) {
//         await dispatch(removeItemFromWishlist(productId));
//       } else {
//         await dispatch(addItemToWishlist(productId));
//       }
//     } catch (error) {
//       console.error("Wishlist operation failed:", error);
//       alert("Failed to update wishlist. Please try again.");
//     }
//   };

//   const fetchProductDetails = (productId) => {
//     navigate(`/product/${productId}`);
//   };

//   useEffect(() => {
//     const fetchProducts = async () => {
//       if (!categoryId) return;

//       setLoading(true);
//       try {
//         const response = await axios.get(
//           `${process.env.REACT_APP_BACKEND_URL}/api/seller/product/get-by-category/${categoryId}`,
//           {
//             headers: {
//               Authorization: `Bearer ${token}`
//             }
//           }
//         );
//         // Filter out inactive products
//         const activeProducts = response.data.products.filter(
//           product => product.status === 'approved'
//         );
//         setProducts(activeProducts || []);
//         setCategory(response?.data?.products[0]?.category?.name);

//         // Fetch ratings for each product
//         const ratingsMap = {};
//         for (const product of activeProducts) {
//           const ratings = await fetchProductRatings(product._id);
//           ratingsMap[product._id] = ratings;
//         }
//         setProductRatings(ratingsMap);
//       } catch (err) {
//         setError(err.message || "Something went wrong.");
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchProducts();
//   }, [categoryId, token]);

//   const nextImage = () => {
//     setCurrentImageIndex(prev => 
//       prev < productDetails.images.length - 1 ? prev + 1 : 0
//     );
//   };

//   const prevImage = () => {
//     setCurrentImageIndex(prev => 
//       prev > 0 ? prev - 1 : productDetails.images.length - 1
//     );
//   };

//   const increaseQuantity = () => {
//     setQuantity(prev => prev + 1);
//   };

//   const decreaseQuantity = () => {
//     if (quantity > 1) {
//       setQuantity(prev => prev - 1);
//     }
//   };

//   const renderRatingStars = (rating) => {
//     const stars = [];
//     const fullStars = Math.floor(rating);
//     const hasHalfStar = rating % 1 >= 0.5;
    
//     for (let i = 0; i < fullStars; i++) {
//       stars.push(<BsStarFill key={`full-${i}`} className="text-yellow-400" />);
//     }
    
//     if (hasHalfStar) {
//       stars.push(<BsStarHalf key="half" className="text-yellow-400" />);
//     }
    
//     const emptyStars = 5 - stars.length;
//     for (let i = 0; i < emptyStars; i++) {
//       stars.push(<BsStar key={`empty-${i}`} className="text-yellow-400" />);
//     }
    
//     return stars;
//   };

//   if (loading) {
//     return (
//       <div className="flex justify-center items-center h-screen">
//         <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="flex justify-center items-center h-screen">
//         <div className="text-red-500 text-lg">Error: {error}</div>
//       </div>
//     );
//   }

//   return (
//     <>
//       <Header/>
//       <div className="container mx-auto px-4 py-8">
//         <h1 className="text-3xl font-bold text-gray-800 mb-8 text-center">{category ? `${category}` : 'Our Products'}</h1>

//         <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
//           {products.map((product) => {
//             const ratings = productRatings[product._id] || [];
//             const avgRating = calculateAverageRating(ratings);
//             const reviewCount = ratings.length;

//             return (
//               <div 
//                 key={product._id} 
//                 className={`bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 ${
//                   product.stock <= 0 ? 'opacity-60' : ''
//                 }`}
//                 onClick={() => fetchProductDetails(product._id)}
//               >
//                 <div className="relative">
//                   <img 
//                     src={product.images[0]?.blob || 'https://via.placeholder.com/300'} 
//                     alt={product.name} 
//                     className="w-full h-48 object-cover cursor-pointer"
//                   />
//                   {product.stock <= 0 && (
//                     <div className="absolute top-0 left-0 w-full h-full bg-gray-100 bg-opacity-50 flex items-center justify-center">
//                       <span className="bg-white px-3 py-1 rounded-md text-sm font-medium text-gray-800">
//                         Out of Stock
//                       </span>
//                     </div>
//                   )}
//                   <button 
//                     onClick={(e) => {
//                       e.stopPropagation();
//                       toggleWishlist(product._id);
//                     }}
//                     disabled={wishlistLoading || product.stock <= 0}
//                     className={`absolute top-2 right-2 p-2 rounded-full transition-colors duration-200 ${
//                       product.stock <= 0 ? 'cursor-not-allowed' : ''
//                     }`}
//                   >
//                     {wishlistLoading ? (
//                       <div className="h-6 w-6 flex items-center justify-center">
//                         <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500"></div>
//                       </div>
//                     ) : wishlist.some(item => item.productId === product._id) ? (
//                       <AiFillHeart className="text-red-500 h-6 w-6" />
//                     ) : (
//                       <AiOutlineHeart className="text-gray-600 h-6 w-6" />
//                     )}
//                   </button>
//                 </div>
                
//                 <div className="p-4">
//                   <h3 className="text-lg font-semibold text-gray-800 mb-1 truncate">{product?.name}</h3>
//                   <p className="text-gray-600 text-sm mb-2 line-clamp-2">{product?.description}</p>
                  
//                   {/* Rating display */}
//                   <div className="flex items-center mb-2">
//                     <div className="flex mr-1">
//                       {renderRatingStars(avgRating)}
//                     </div>
//                     <span className="text-xs text-gray-500">
//                       ({reviewCount})
//                     </span>
//                   </div>
                  
//                   <div className="flex justify-between items-center mt-2">
//                     <span className="text-lg font-bold text-blue-600">
//                       ₹{product?.price?.toFixed(2) || '0.00'}
//                     </span>
//                     {product.stock > 0 ? (
//                       <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
//                         In Stock
//                       </span>
//                     ) : (
//                       <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">
//                         Out of Stock
//                       </span>
//                     )}
//                   </div>
                  
//                   <button 
//                     className={`mt-4 w-full py-2 px-4 rounded-md transition-colors duration-200 ${
//                       product.stock > 0 
//                         ? 'bg-blue-600 hover:bg-blue-700 text-white' 
//                         : 'bg-gray-300 text-gray-500 cursor-not-allowed'
//                     }`}
//                     onClick={(e) => {
//                       e.stopPropagation();
//                       handleAddToCart(product);
//                     }}
//                     disabled={product?.stock <= 0}
//                   >
//                     {product.stock > 0 ? 'Add to Cart' : 'Out of Stock'}
//                   </button>
//                 </div>
//               </div>
//             );
//           })}
//         </div>

//         {products.length === 0 && !loading && (
//           <div className="text-center py-12">
//             <p className="text-gray-500 text-lg">No active products found in this category.</p>
//           </div>
//         )}
//       </div>
//     </>
//   );
// };

// export default ProductsByCategory;

// import React, { useState, useEffect } from 'react';
// import axios from 'axios';
// import Cookies from 'js-cookie';
// import { useParams } from 'react-router-dom';
// import { AiOutlineHeart, AiFillHeart, AiOutlineClose, AiOutlineShoppingCart } from 'react-icons/ai';
// import { BsStarFill, BsStarHalf, BsStar } from 'react-icons/bs';
// import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
// import Header from '../Header/Header';
// import { useDispatch } from 'react-redux';
// import { useNavigate } from 'react-router-dom';
// import { addToCart } from '../../redux/slices/cartSlice'; 

// const ProductsByCategory = () => {
//   const { categoryId } = useParams();
//   const [products, setProducts] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(null);
//   const [wishlist, setWishlist] = useState([]);
//   const [category, setCategory] = useState(null);
//   const [wishlistLoading, setWishlistLoading] = useState({});
//   const [selectedProduct, setSelectedProduct] = useState(null);
//   const [productDetails, setProductDetails] = useState(null);
//   const [productLoading, setProductLoading] = useState(false);
//   const [currentImageIndex, setCurrentImageIndex] = useState(0);
//   const [quantity, setQuantity] = useState(1);
//   const [productRatings, setProductRatings] = useState({}); // Store ratings for each product
//   const dispatch = useDispatch();
//   const navigate = useNavigate();

//   const token = Cookies.get("ecom_user_token");

//   // Fetch initial wishlist when component mounts
//   useEffect(() => {
//     const fetchWishlist = async () => {
//       try {
//         const response = await axios.get(
//           'https://seller-backend-29o9.onrender.com/api/buyer/get-wishlist',
//           {
//             headers: {
//               Authorization: `Bearer ${token}`
//             }
//           }
//         );
//         const productIds = response.data.wishlist.map(item => item.productId._id);
//         setWishlist(productIds);
//       } catch (err) {
//         console.error("Failed to fetch wishlist:", err);
//       }
//     };

//     if (token) {
//       fetchWishlist();
//     }
//   }, [token]);

//   // Function to fetch ratings for a product
//   const fetchProductRatings = async (productId) => {
//     try {
//       const response = await axios.get(
//         `https://seller-backend-29o9.onrender.com/api/rating/productRating/${productId}`,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`
//           }
//         }
//       );
//       const ratings = response.data.ratings || [];
//       return ratings;
//     } catch (error) {
//       console.error("Failed to fetch ratings:", error);
//       return [];
//     }
//   };

//   // Calculate average rating for a product
//   const calculateAverageRating = (ratings) => {
//     if (ratings.length === 0) return 0;
//     const sum = ratings.reduce((acc, review) => acc + review.ratings, 0);
//     return sum / ratings.length;
//   };

//   const handleAddToCart = async (product) => {
//     if (product.stock <= 0) {
//       alert("This product is out of stock");
//       return;
//     }
    
//     try {
//       const response = await axios.post(
//         'https://seller-backend-29o9.onrender.com/api/buyer/add-to-cart',
//         {
//           productId: product._id,
//           quantity: quantity
//         },
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             'Content-Type': 'application/json'
//           }
//         }
//       );
//       navigate('/cart');
//     } catch (error) {
//       console.error("Add to cart API failed:", error);
//       alert("Failed to add to cart. Please try again.");
//     }
//   };

//   const toggleWishlist = async (productId) => {
//     if (!token) {
//       alert("Please login to manage your wishlist");
//       navigate('/login');
//       return;
//     }

//     setWishlistLoading(prev => ({ ...prev, [productId]: true }));

//     try {
//       if (wishlist.includes(productId)) {
//         await axios.delete(
//           `https://seller-backend-29o9.onrender.com/api/buyer/remove-from-wishlist/${productId}`,
//           {
//             headers: {
//               Authorization: `Bearer ${token}`
//             }
//           }
//         );
//         setWishlist(wishlist.filter(id => id !== productId));
//       } else {
//         await axios.post(
//           'https://seller-backend-29o9.onrender.com/api/buyer/add-to-wishlist',
//           { productId },
//           {
//             headers: {
//               Authorization: `Bearer ${token}`,
//               'Content-Type': 'application/json'
//             }
//           }
//         );
//         setWishlist([...wishlist, productId]);
//       }
//     } catch (error) {
//       console.error("Wishlist operation failed:", error);
//       alert("Failed to update wishlist. Please try again.");
//     } finally {
//       setWishlistLoading(prev => ({ ...prev, [productId]: false }));
//     }
//   };

//   const fetchProductDetails = (productId) => {
//     navigate(`/product/${productId}`);
//   };

//   useEffect(() => {
//     const fetchProducts = async () => {
//       if (!categoryId) return;

//       setLoading(true);
//       try {
//         const response = await axios.get(
//           `${process.env.REACT_APP_BACKEND_URL}/api/seller/product/get-by-category/${categoryId}`,
//           {
//             headers: {
//               Authorization: `Bearer ${token}`
//             }
//           }
//         );
//         // Filter out inactive products
//         const activeProducts = response.data.products.filter(
//           product => product.status === 'approved'
//         );
//         setProducts(activeProducts || []);
//         setCategory(response?.data?.products[0]?.category?.name);

//         // Fetch ratings for each product
//         const ratingsMap = {};
//         for (const product of activeProducts) {
//           const ratings = await fetchProductRatings(product._id);
//           ratingsMap[product._id] = ratings;
//         }
//         setProductRatings(ratingsMap);
//       } catch (err) {
//         setError(err.message || "Something went wrong.");
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchProducts();
//   }, [categoryId, token]);

//   const nextImage = () => {
//     setCurrentImageIndex(prev => 
//       prev < productDetails.images.length - 1 ? prev + 1 : 0
//     );
//   };

//   const prevImage = () => {
//     setCurrentImageIndex(prev => 
//       prev > 0 ? prev - 1 : productDetails.images.length - 1
//     );
//   };

//   const increaseQuantity = () => {
//     setQuantity(prev => prev + 1);
//   };

//   const decreaseQuantity = () => {
//     if (quantity > 1) {
//       setQuantity(prev => prev - 1);
//     }
//   };

//   const renderRatingStars = (rating) => {
//     const stars = [];
//     const fullStars = Math.floor(rating);
//     const hasHalfStar = rating % 1 >= 0.5;
    
//     for (let i = 0; i < fullStars; i++) {
//       stars.push(<BsStarFill key={`full-${i}`} className="text-yellow-400" />);
//     }
    
//     if (hasHalfStar) {
//       stars.push(<BsStarHalf key="half" className="text-yellow-400" />);
//     }
    
//     const emptyStars = 5 - stars.length;
//     for (let i = 0; i < emptyStars; i++) {
//       stars.push(<BsStar key={`empty-${i}`} className="text-yellow-400" />);
//     }
    
//     return stars;
//   };

//   if (loading) {
//     return (
//       <div className="flex justify-center items-center h-screen">
//         <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="flex justify-center items-center h-screen">
//         <div className="text-red-500 text-lg">Error: {error}</div>
//       </div>
//     );
//   }

//   return (
//     <>
//       <Header/>
//       <div className="container mx-auto px-4 py-8">
//         <h1 className="text-3xl font-bold text-gray-800 mb-8 text-center">{category ? `${category}` : 'Our Products'}</h1>

//         <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
//           {products.map((product) => {
//             const ratings = productRatings[product._id] || [];
//             const avgRating = calculateAverageRating(ratings);
//             const reviewCount = ratings.length;

//             return (
//               <div 
//                 key={product._id} 
//                 className={`bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 ${
//                   product.stock <= 0 ? 'opacity-60' : ''
//                 }`}
//                 onClick={() => fetchProductDetails(product._id)}
//               >
//                 <div className="relative">
//                   <img 
//                     src={product.images[0]?.blob || 'https://via.placeholder.com/300'} 
//                     alt={product.name} 
//                     className="w-full h-48 object-cover cursor-pointer"
//                   />
//                   {product.stock <= 0 && (
//                     <div className="absolute top-0 left-0 w-full h-full bg-gray-100 bg-opacity-50 flex items-center justify-center">
//                       <span className="bg-white px-3 py-1 rounded-md text-sm font-medium text-gray-800">
//                         Out of Stock
//                       </span>
//                     </div>
//                   )}
//                   <button 
//                     onClick={(e) => {
//                       e.stopPropagation();
//                       toggleWishlist(product._id);
//                     }}
//                     disabled={wishlistLoading[product._id] || product.stock <= 0}
//                     className={`absolute top-2 right-2 p-2 rounded-full transition-colors duration-200 ${
//                       product.stock <= 0 ? 'cursor-not-allowed' : ''
//                     }`}
//                   >
//                     {wishlistLoading[product._id] ? (
//                       <div className="h-6 w-6 flex items-center justify-center">
//                         <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500"></div>
//                       </div>
//                     ) : wishlist.includes(product._id) ? (
//                       <AiFillHeart className="text-red-500 h-6 w-6" />
//                     ) : (
//                       <AiOutlineHeart className="text-gray-600 h-6 w-6" />
//                     )}
//                   </button>
//                 </div>
                
//                 <div className="p-4">
//                   <h3 className="text-lg font-semibold text-gray-800 mb-1 truncate">{product?.name}</h3>
//                   <p className="text-gray-600 text-sm mb-2 line-clamp-2">{product?.description}</p>
                  
//                   {/* Rating display */}
//                   <div className="flex items-center mb-2">
//                     <div className="flex mr-1">
//                       {renderRatingStars(avgRating)}
//                     </div>
//                     <span className="text-xs text-gray-500">
//                       ({reviewCount})
//                     </span>
//                   </div>
                  
//                   <div className="flex justify-between items-center mt-2">
//                     <span className="text-lg font-bold text-blue-600">
//                       ₹{product?.price?.toFixed(2) || '0.00'}
//                     </span>
//                     {product.stock > 0 ? (
//                       <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
//                         In Stock
//                       </span>
//                     ) : (
//                       <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">
//                         Out of Stock
//                       </span>
//                     )}
//                   </div>
                  
//                   <button 
//                     className={`mt-4 w-full py-2 px-4 rounded-md transition-colors duration-200 ${
//                       product.stock > 0 
//                         ? 'bg-blue-600 hover:bg-blue-700 text-white' 
//                         : 'bg-gray-300 text-gray-500 cursor-not-allowed'
//                     }`}
//                     onClick={(e) => {
//                       e.stopPropagation();
//                       handleAddToCart(product);
//                     }}
//                     disabled={product?.stock <= 0}
//                   >
//                     {product.stock > 0 ? 'Add to Cart' : 'Out of Stock'}
//                   </button>
//                 </div>
//               </div>
//             );
//           })}
//         </div>

//         {products.length === 0 && !loading && (
//           <div className="text-center py-12">
//             <p className="text-gray-500 text-lg">No active products found in this category.</p>
//           </div>
//         )}
//       </div>
//     </>
//   );
// };

// export default ProductsByCategory;

// import React, { useState, useEffect } from 'react';
// import axios from 'axios';
// import Cookies from 'js-cookie';
// import { useParams } from 'react-router-dom';
// import { AiOutlineHeart, AiFillHeart, AiOutlineClose, AiOutlineShoppingCart } from 'react-icons/ai';
// import { BsStarFill, BsStarHalf, BsStar } from 'react-icons/bs';
// import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
// import Header from '../Header/Header';
// import { useDispatch } from 'react-redux';
// import { useNavigate } from 'react-router-dom';
// import { addToCart } from '../../redux/slices/cartSlice'; 

// const ProductsByCategory = () => {
//   const { categoryId } = useParams();
//   const [products, setProducts] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(null);
//   const [wishlist, setWishlist] = useState([]);
//   const [category, setCategory] = useState(null);
//   const [wishlistLoading, setWishlistLoading] = useState({});
//   const [selectedProduct, setSelectedProduct] = useState(null);
//   const [productDetails, setProductDetails] = useState(null);
//   const [productLoading, setProductLoading] = useState(false);
//   const [currentImageIndex, setCurrentImageIndex] = useState(0);
//   const [quantity, setQuantity] = useState(1);
//   const dispatch = useDispatch();
//   const navigate = useNavigate();

//   const token = Cookies.get("ecom_user_token");

//   // Fetch initial wishlist when component mounts
//   useEffect(() => {
//     const fetchWishlist = async () => {
//       try {
//         const response = await axios.get(
//           'https://seller-backend-29o9.onrender.com/api/buyer/get-wishlist',
//           {
//             headers: {
//               Authorization: `Bearer ${token}`
//             }
//           }
//         );
//         const productIds = response.data.wishlist.map(item => item.productId._id);
//         setWishlist(productIds);
//       } catch (err) {
//         console.error("Failed to fetch wishlist:", err);
//       }
//     };

//     if (token) {
//       fetchWishlist();
//     }
//   }, [token]);

//   const handleAddToCart = async (product) => {
//     if (product.stock <= 0) {
//       alert("This product is out of stock");
//       return;
//     }
    
//     try {
//       const response = await axios.post(
//         'https://seller-backend-29o9.onrender.com/api/buyer/add-to-cart',
//         {
//           productId: product._id,
//           quantity: quantity
//         },
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             'Content-Type': 'application/json'
//           }
//         }
//       );
//       navigate('/cart');
//     } catch (error) {
//       console.error("Add to cart API failed:", error);
//       alert("Failed to add to cart. Please try again.");
//     }
//   };

//   const toggleWishlist = async (productId) => {
//     if (!token) {
//       alert("Please login to manage your wishlist");
//       navigate('/login');
//       return;
//     }

//     setWishlistLoading(prev => ({ ...prev, [productId]: true }));

//     try {
//       if (wishlist.includes(productId)) {
//         await axios.delete(
//           `https://seller-backend-29o9.onrender.com/api/buyer/remove-from-wishlist/${productId}`,
//           {
//             headers: {
//               Authorization: `Bearer ${token}`
//             }
//           }
//         );
//         setWishlist(wishlist.filter(id => id !== productId));
//       } else {
//         await axios.post(
//           'https://seller-backend-29o9.onrender.com/api/buyer/add-to-wishlist',
//           { productId },
//           {
//             headers: {
//               Authorization: `Bearer ${token}`,
//               'Content-Type': 'application/json'
//             }
//           }
//         );
//         setWishlist([...wishlist, productId]);
//       }
//     } catch (error) {
//       console.error("Wishlist operation failed:", error);
//       alert("Failed to update wishlist. Please try again.");
//     } finally {
//       setWishlistLoading(prev => ({ ...prev, [productId]: false }));
//     }
//   };

//   const fetchProductDetails = (productId) => {
//     navigate(`/product/${productId}`);
//   };

//   useEffect(() => {
//     const fetchProducts = async () => {
//       if (!categoryId) return;

//       setLoading(true);
//       try {
//         const response = await axios.get(
//           `${process.env.REACT_APP_BACKEND_URL}/api/seller/product/get-by-category/${categoryId}`,
//           {
//             headers: {
//               Authorization: `Bearer ${token}`
//             }
//           }
//         );
//         // Filter out inactive products
//         const activeProducts = response.data.products.filter(
//           product => product.status === 'active'
//         );
//         setProducts(activeProducts || []);
//         setCategory(response?.data?.products[0]?.category?.name)
//       } catch (err) {
//         setError(err.message || "Something went wrong.");
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchProducts();
//   }, [categoryId, token]);

//   const nextImage = () => {
//     setCurrentImageIndex(prev => 
//       prev < productDetails.images.length - 1 ? prev + 1 : 0
//     );
//   };

//   const prevImage = () => {
//     setCurrentImageIndex(prev => 
//       prev > 0 ? prev - 1 : productDetails.images.length - 1
//     );
//   };

//   const increaseQuantity = () => {
//     setQuantity(prev => prev + 1);
//   };

//   const decreaseQuantity = () => {
//     if (quantity > 1) {
//       setQuantity(prev => prev - 1);
//     }
//   };

//   const renderRatingStars = (rating) => {
//     const stars = [];
//     const fullStars = Math.floor(rating);
//     const hasHalfStar = rating % 1 >= 0.5;
    
//     for (let i = 0; i < fullStars; i++) {
//       stars.push(<BsStarFill key={`full-${i}`} className="text-yellow-400" />);
//     }
    
//     if (hasHalfStar) {
//       stars.push(<BsStarHalf key="half" className="text-yellow-400" />);
//     }
    
//     const emptyStars = 5 - stars.length;
//     for (let i = 0; i < emptyStars; i++) {
//       stars.push(<BsStar key={`empty-${i}`} className="text-yellow-400" />);
//     }
    
//     return stars;
//   };

//   if (loading) {
//     return (
//       <div className="flex justify-center items-center h-screen">
//         <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="flex justify-center items-center h-screen">
//         <div className="text-red-500 text-lg">Error: {error}</div>
//       </div>
//     );
//   }

//   return (
//     <>
//       <Header/>
//       <div className="container mx-auto px-4 py-8">
//         <h1 className="text-3xl font-bold text-gray-800 mb-8 text-center">{category ? `${category}` : 'Our Products'}</h1>

//         <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
//           {products.map((product) => (
//             <div 
//               key={product._id} 
//               className={`bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 ${
//                 product.stock <= 0 ? 'opacity-60' : ''
//               }`}
//               onClick={() => fetchProductDetails(product._id)}
//             >
//               <div className="relative">
//                 <img 
//                   src={product.images[0]?.blob || 'https://via.placeholder.com/300'} 
//                   alt={product.name} 
//                   className="w-full h-48 object-cover cursor-pointer"
//                 />
//                 {product.stock <= 0 && (
//                   <div className="absolute top-0 left-0 w-full h-full bg-gray-100 bg-opacity-50 flex items-center justify-center">
//                     <span className="bg-white px-3 py-1 rounded-md text-sm font-medium text-gray-800">
//                       Out of Stock
//                     </span>
//                   </div>
//                 )}
//                 <button 
//                   onClick={(e) => {
//                     e.stopPropagation();
//                     toggleWishlist(product._id);
//                   }}
//                   disabled={wishlistLoading[product._id] || product.stock <= 0}
//                   className={`absolute top-2 right-2 p-2 rounded-full transition-colors duration-200 ${
//                     product.stock <= 0 ? 'cursor-not-allowed' : ''
//                   }`}
//                 >
//                   {wishlistLoading[product._id] ? (
//                     <div className="h-6 w-6 flex items-center justify-center">
//                       <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500"></div>
//                     </div>
//                   ) : wishlist.includes(product._id) ? (
//                     <AiFillHeart className="text-red-500 h-6 w-6" />
//                   ) : (
//                     <AiOutlineHeart className="text-gray-600 h-6 w-6" />
//                   )}
//                 </button>
//               </div>
              
//               <div className="p-4">
//                 <h3 className="text-lg font-semibold text-gray-800 mb-1 truncate">{product?.name}</h3>
//                 <p className="text-gray-600 text-sm mb-2 line-clamp-2">{product?.description}</p>
                
//                 <div className="flex justify-between items-center mt-4">
//                   <span className="text-lg font-bold text-blue-600">
//                     ${product?.price?.toFixed(2) || '0.00'}
//                   </span>
//                   {product.stock > 0 ? (
//                     <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
//                       In Stock
//                     </span>
//                   ) : (
//                     <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">
//                       Out of Stock
//                     </span>
//                   )}
//                 </div>
                
//                 <button 
//                   className={`mt-4 w-full py-2 px-4 rounded-md transition-colors duration-200 ${
//                     product.stock > 0 
//                       ? 'bg-blue-600 hover:bg-blue-700 text-white' 
//                       : 'bg-gray-300 text-gray-500 cursor-not-allowed'
//                   }`}
//                   onClick={(e) => {
//                     e.stopPropagation();
//                     handleAddToCart(product);
//                   }}
//                   disabled={product?.stock <= 0}
//                 >
//                   {product.stock > 0 ? 'Add to Cart' : 'Out of Stock'}
//                 </button>
//               </div>
//             </div>
//           ))}
//         </div>

//         {products.length === 0 && !loading && (
//           <div className="text-center py-12">
//             <p className="text-gray-500 text-lg">No active products found in this category.</p>
//           </div>
//         )}
//       </div>
//     </>
//   );
// };

// export default ProductsByCategory;

// import React, { useState, useEffect } from 'react';
// import axios from 'axios';
// import Cookies from 'js-cookie';
// import { useParams } from 'react-router-dom';
// import { AiOutlineHeart, AiFillHeart, AiOutlineClose, AiOutlineShoppingCart } from 'react-icons/ai';
// import { BsStarFill, BsStarHalf, BsStar } from 'react-icons/bs';
// import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
// import Header from '../Header/Header';
// import { useDispatch } from 'react-redux';
// import { useNavigate } from 'react-router-dom';
// import { addToCart } from '../../redux/slices/cartSlice'; 

// const ProductsByCategory = () => {
//   const { categoryId } = useParams();
//   const [products, setProducts] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(null);
//   const [wishlist, setWishlist] = useState([]);
//   const [category, setCategory] = useState(null);
//   const [wishlistLoading, setWishlistLoading] = useState({});
//   const [selectedProduct, setSelectedProduct] = useState(null);
//   const [productDetails, setProductDetails] = useState(null);
//   const [productLoading, setProductLoading] = useState(false);
//   const [currentImageIndex, setCurrentImageIndex] = useState(0);
//   const [quantity, setQuantity] = useState(1);
//   const dispatch = useDispatch();
//   const navigate = useNavigate();

//   const token = Cookies.get("ecom_user_token");

//   // Fetch initial wishlist when component mounts
//   useEffect(() => {
//     const fetchWishlist = async () => {
//       try {
//         const response = await axios.get(
//           'https://seller-backend-29o9.onrender.com/api/buyer/get-wishlist',
//           {
//             headers: {
//               Authorization: `Bearer ${token}`
//             }
//           }
//         );
//         const productIds = response.data.wishlist.map(item => item.productId._id);
//         setWishlist(productIds);
//       } catch (err) {
//         console.error("Failed to fetch wishlist:", err);
//       }
//     };

//     if (token) {
//       fetchWishlist();
//     }
//   }, [token]);

//   const handleAddToCart = async (product) => {
//     if (product.stock <= 0) {
//       alert("This product is out of stock");
//       return;
//     }
    
//     try {
//       const response = await axios.post(
//         'https://seller-backend-29o9.onrender.com/api/buyer/add-to-cart',
//         {
//           productId: product._id,
//           quantity: quantity
//         },
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             'Content-Type': 'application/json'
//           }
//         }
//       );
//       navigate('/cart');
//     } catch (error) {
//       console.error("Add to cart API failed:", error);
//       alert("Failed to add to cart. Please try again.");
//     }
//   };

//   const toggleWishlist = async (productId) => {
//     if (!token) {
//       alert("Please login to manage your wishlist");
//       navigate('/login');
//       return;
//     }

//     setWishlistLoading(prev => ({ ...prev, [productId]: true }));

//     try {
//       if (wishlist.includes(productId)) {
//         await axios.delete(
//           `https://seller-backend-29o9.onrender.com/api/buyer/remove-from-wishlist/${productId}`,
//           {
//             headers: {
//               Authorization: `Bearer ${token}`
//             }
//           }
//         );
//         setWishlist(wishlist.filter(id => id !== productId));
//       } else {
//         await axios.post(
//           'https://seller-backend-29o9.onrender.com/api/buyer/add-to-wishlist',
//           { productId },
//           {
//             headers: {
//               Authorization: `Bearer ${token}`,
//               'Content-Type': 'application/json'
//             }
//           }
//         );
//         setWishlist([...wishlist, productId]);
//       }
//     } catch (error) {
//       console.error("Wishlist operation failed:", error);
//       alert("Failed to update wishlist. Please try again.");
//     } finally {
//       setWishlistLoading(prev => ({ ...prev, [productId]: false }));
//     }
//   };

//   // const fetchProductDetails = async (productId) => {
//   //   setProductLoading(true);
//   //   try {
//   //     const response = await axios.get(
//   //       `https://seller-backend-29o9.onrender.com/api/seller/product/getById/${productId}`,
//   //       {
//   //         headers: {
//   //           Authorization: `Bearer ${token}`
//   //         }
//   //       }
//   //     );
//   //     setProductDetails(response.data.product);
//   //     setSelectedProduct(response.data.product);
//   //     setCurrentImageIndex(0);
//   //     setQuantity(1);
//   //   } catch (error) {
//   //     console.error("Failed to fetch product details:", error);
//   //     alert("Failed to load product details. Please try again.");
//   //   } finally {
//   //     setProductLoading(false);
//   //   }
//   // };

//   useEffect(() => {
//     const fetchProducts = async () => {
//       if (!categoryId) return;

//       setLoading(true);
//       try {
//         const response = await axios.get(
//           `${process.env.REACT_APP_BACKEND_URL}/api/seller/product/get-by-category/${categoryId}`,
//           {
//             headers: {
//               Authorization: `Bearer ${token}`
//             }
//           }
//         );
//         // Filter out inactive products
//         const activeProducts = response.data.products.filter(
//           product => product.status === 'active'
//         );
//         setProducts(activeProducts || []);
//         setCategory(response?.data?.products[0]?.category?.name)
//       } catch (err) {
//         setError(err.message || "Something went wrong.");
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchProducts();
//   }, [categoryId, token]);

//   const nextImage = () => {
//     setCurrentImageIndex(prev => 
//       prev < productDetails.images.length - 1 ? prev + 1 : 0
//     );
//   };

//   const prevImage = () => {
//     setCurrentImageIndex(prev => 
//       prev > 0 ? prev - 1 : productDetails.images.length - 1
//     );
//   };

//   const increaseQuantity = () => {
//     setQuantity(prev => prev + 1);
//   };

//   const decreaseQuantity = () => {
//     if (quantity > 1) {
//       setQuantity(prev => prev - 1);
//     }
//   };

//   const renderRatingStars = (rating) => {
//     const stars = [];
//     const fullStars = Math.floor(rating);
//     const hasHalfStar = rating % 1 >= 0.5;
    
//     for (let i = 0; i < fullStars; i++) {
//       stars.push(<BsStarFill key={`full-${i}`} className="text-yellow-400" />);
//     }
    
//     if (hasHalfStar) {
//       stars.push(<BsStarHalf key="half" className="text-yellow-400" />);
//     }
    
//     const emptyStars = 5 - stars.length;
//     for (let i = 0; i < emptyStars; i++) {
//       stars.push(<BsStar key={`empty-${i}`} className="text-yellow-400" />);
//     }
    
//     return stars;
//   };

//   if (loading) {
//     return (
//       <div className="flex justify-center items-center h-screen">
//         <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="flex justify-center items-center h-screen">
//         <div className="text-red-500 text-lg">Error: {error}</div>
//       </div>
//     );
//   }
//   const fetchProductDetails = (productId) => {
//   navigate(`/product/${productId}`);
// };

//   return (
//     <>
//       <Header/>
//       <div className="container mx-auto px-4 py-8">
//         <h1 className="text-3xl font-bold text-gray-800 mb-8 text-center">{category ? `${category}` : 'Our Products'}</h1>

//         <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
//           {products.map((product) => (
//             <div 
//               key={product._id} 
//               className={`bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 ${
//                 product.stock <= 0 ? 'opacity-60' : ''
//               }`}
//               onClick={() => fetchProductDetails(product._id)}
//             >
//               <div className="relative">
//                 <img 
//                   src={product.images[0]?.blob || 'https://via.placeholder.com/300'} 
//                   alt={product.name} 
//                   className="w-full h-48 object-cover cursor-pointer"
//                 />
//                 {product.stock <= 0 && (
//                   <div className="absolute top-0 left-0 w-full h-full bg-gray-100 bg-opacity-50 flex items-center justify-center">
//                     <span className="bg-white px-3 py-1 rounded-md text-sm font-medium text-gray-800">
//                       Out of Stock
//                     </span>
//                   </div>
//                 )}
//                 <button 
//                   onClick={(e) => {
//                     e.stopPropagation();
//                     toggleWishlist(product._id);
//                   }}
//                   disabled={wishlistLoading[product._id] || product.stock <= 0}
//                   className={`absolute top-2 right-2 p-2 rounded-full transition-colors duration-200 ${
//                     product.stock <= 0 ? 'cursor-not-allowed' : ''
//                   }`}
//                 >
//                   {wishlistLoading[product._id] ? (
//                     <div className="h-6 w-6 flex items-center justify-center">
//                       <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500"></div>
//                     </div>
//                   ) : wishlist.includes(product._id) ? (
//                     <AiFillHeart className="text-red-500 h-6 w-6" />
//                   ) : (
//                     <AiOutlineHeart className="text-gray-600 h-6 w-6" />
//                   )}
//                 </button>
//               </div>
              
//               <div className="p-4">
//                 <h3 className="text-lg font-semibold text-gray-800 mb-1 truncate">{product?.name}</h3>
//                 <p className="text-gray-600 text-sm mb-2 line-clamp-2">{product?.description}</p>
                
//                 <div className="flex justify-between items-center mt-4">
//                   <span className="text-lg font-bold text-blue-600">
//                     ${product?.price?.toFixed(2) || '0.00'}
//                   </span>
//                   {product.stock > 0 ? (
//                     <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
//                       In Stock
//                     </span>
//                   ) : (
//                     <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">
//                       Out of Stock
//                     </span>
//                   )}
//                 </div>
                
//                 <button 
//                   className={`mt-4 w-full py-2 px-4 rounded-md transition-colors duration-200 ${
//                     product.stock > 0 
//                       ? 'bg-blue-600 hover:bg-blue-700 text-white' 
//                       : 'bg-gray-300 text-gray-500 cursor-not-allowed'
//                   }`}
//                   onClick={(e) => {
//                     e.stopPropagation();
//                     handleAddToCart(product);
//                   }}
//                   disabled={product?.stock <= 0}
//                 >
//                   {product.stock > 0 ? 'Add to Cart' : 'Out of Stock'}
//                 </button>
//               </div>
//             </div>
//           ))}
//         </div>

//         {products.length === 0 && !loading && (
//           <div className="text-center py-12">
//             <p className="text-gray-500 text-lg">No active products found in this category.</p>
//           </div>
//         )}
//       </div>

//       {/* Product Details Modal
//       {selectedProduct && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
//           <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
//             {productLoading ? (
//               <div className="flex justify-center items-center h-96">
//                 <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
//               </div>
//             ) : (
//               <div className="p-6">
//                 <div className="flex justify-between items-start mb-4">
//                   <h2 className="text-2xl font-bold text-gray-800">{productDetails.name}</h2>
//                   <button 
//                     onClick={() => setSelectedProduct(null)}
//                     className="text-gray-500 hover:text-gray-700"
//                   >
//                     <AiOutlineClose size={24} />
//                   </button>
//                 </div>

//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
//                   {/* Product Images */}
//                   <div className="relative">
//                     <div className="relative h-96 bg-gray-100 rounded-lg overflow-hidden">
//                       <img
//                         src={productDetails.images[currentImageIndex]?.blob || 'https://via.placeholder.com/600'}
//                         alt={productDetails.name}
//                         className="w-full h-full object-contain"
//                       />
//                       {productDetails.images.length > 1 && (
//                         <>
//                           <button
//                             onClick={(e) => {
//                               e.stopPropagation();
//                               prevImage();
//                             }}
//                             className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white rounded-full p-2 shadow-md hover:bg-gray-100"
//                           >
//                             <FiChevronLeft size={20} />
//                           </button>
//                           <button
//                             onClick={(e) => {
//                               e.stopPropagation();
//                               nextImage();
//                             }}
//                             className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white rounded-full p-2 shadow-md hover:bg-gray-100"
//                           >
//                             <FiChevronRight size={20} />
//                           </button>
//                         </>
//                       )}
//                     </div>
//                     <div className="flex mt-4 space-x-2 overflow-x-auto">
//                       {productDetails.images.map((image, index) => (
//                         <button
//                           key={index}
//                           onClick={() => setCurrentImageIndex(index)}
//                           className={`w-16 h-16 rounded border-2 ${currentImageIndex === index ? 'border-blue-500' : 'border-transparent'}`}
//                         >
//                           <img
//                             src={image?.blob || 'https://via.placeholder.com/100'}
//                             alt={`Thumbnail ${index + 1}`}
//                             className="w-full h-full object-cover"
//                           />
//                         </button>
//                       ))}
//                     </div>
//                   </div>

//                   {/* Product Info */}
//                   <div>
//                     <div className="flex items-center mb-4">
//                       <div className="flex mr-2">
//                         {renderRatingStars(4.5)} {/* Assuming 4.5 rating for demo */}
//                       </div>
//                       <span className="text-sm text-gray-500">(42 reviews)</span>
//                     </div>

//                     <div className="mb-6">
//                       <span className="text-3xl font-bold text-gray-800">${productDetails.price.toFixed(2)}</span>
//                       {productDetails.originalPrice && (
//                         <span className="ml-2 text-lg text-gray-500 line-through">${productDetails.originalPrice.toFixed(2)}</span>
//                       )}
//                       {productDetails.discount && (
//                         <span className="ml-2 text-green-600 font-medium">{productDetails.discount}% OFF</span>
//                       )}
//                     </div>

//                     <div className="mb-6">
//                       <h3 className="text-lg font-semibold mb-2">Description</h3>
//                       <p className="text-gray-600">{productDetails.description}</p>
//                     </div>

//                     {/* <div className="mb-6">
//                       <h3 className="text-lg font-semibold mb-2">Key Features</h3>
//                       <ul className="list-disc pl-5 text-gray-600 space-y-1">
//                         {productDetails.description.split('. ').filter(point => point.trim() !== '').map((point, index) => (
//                           <li key={index}>{point}.</li>
//                         ))}
//                       </ul>
//                     </div> */}

//                     <div className="mb-6">
//                       <div className="flex items-center space-x-4 mb-2">
//                         <span className="font-medium">Brand:</span>
//                         <span className="text-gray-600">{productDetails.brand?.name || 'N/A'}</span>
//                       </div>
//                       <div className="flex items-center space-x-4 mb-2">
//                         <span className="font-medium">SKU:</span>
//                         <span className="text-gray-600">{productDetails.sku || 'N/A'}</span>
//                       </div>
//                       <div className="flex items-center space-x-4 mb-2">
//                         <span className="font-medium">Availability:</span>
//                         <span className={productDetails.stock > 0 ? 'text-green-600' : 'text-red-600'}>
//                           {productDetails.stock > 0 ? 'In Stock' : 'Out of Stock'}
//                         </span>
//                       </div>
//                       {/* <div className="flex items-center space-x-4">
//                         <span className="font-medium">Seller:</span>
//                         <span className="text-gray-600">{productDetails.seller?.businessName || 'N/A'}</span>
//                       </div> */}
//                     </div>

//                     {productDetails.stock > 0 && (
//                       <div className="mb-6">
//                         {/* <div className="flex items-center space-x-4 mb-4">
//                           <span className="font-medium">Quantity:</span>
//                           <div className="flex items-center border rounded-md">
//                             <button
//                               onClick={decreaseQuantity}
//                               className="px-3 py-1 text-lg hover:bg-gray-100"
//                               disabled={quantity <= 1}
//                             >
//                               -
//                             </button>
//                             <span className="px-4 py-1 border-x">{quantity}</span>
//                             <button
//                               onClick={increaseQuantity}
//                               className="px-3 py-1 text-lg hover:bg-gray-100"
//                             >
//                               +
//                             </button>
//                           </div>
//                           <span className="text-sm text-gray-500">{productDetails.stock} available</span>
//                         </div> */}

//                         <div className="flex space-x-4">
//                           <button
//                             onClick={() => handleAddToCart(productDetails)}
//                             className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-md flex items-center justify-center space-x-2"
//                           >
//                             <AiOutlineShoppingCart size={20} />
//                             <span>Add to Cart</span>
//                           </button>
//                           <button
//                             onClick={() => {
//                               toggleWishlist(productDetails._id);
//                             }}
//                             className="w-12 flex items-center justify-center border border-gray-300 rounded-md hover:bg-gray-100"
//                           >
//                             {wishlistLoading[productDetails._id] ? (
//                               <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-blue-500"></div>
//                             ) : wishlist.includes(productDetails._id) ? (
//                               <AiFillHeart className="text-red-500 h-6 w-6" />
//                             ) : (
//                               <AiOutlineHeart className="text-gray-600 h-6 w-6" />
//                             )}
//                           </button>
//                         </div>
//                       </div>
//                     )}

//                     {productDetails.stock <= 0 && (
//                       <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6">
//                         <p>This product is currently out of stock.</p>
//                         {productDetails.backorder_allowed && (
//                           <p className="mt-2">Backorders are allowed - you can still place an order!</p>
//                         )}
//                       </div>
//                     )}
//                   </div>
//                 </div>

//                 {/* Additional Information */}
//                 {/* <div className="mt-8 border-t pt-6">
//                   <h3 className="text-xl font-semibold mb-4">Additional Information</h3>
//                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                     <div>
//                       <h4 className="font-medium mb-2">Shipping Information</h4>
//                       <p className="text-gray-600">
//                         {productDetails.ShippingPackage?.packageName ? 
//                           `Shipped in ${productDetails.ShippingPackage.packageName}` : 
//                           'Standard shipping'}
//                       </p>
//                     </div>
//                     <div>
//                       <h4 className="font-medium mb-2">Location</h4>
//                       <p className="text-gray-600">
//                         {productDetails.state}, {productDetails.country}
//                       </p>
//                     </div>
//                     {productDetails.attributes && productDetails.attributes.length > 0 && (
//                       <div>
//                         <h4 className="font-medium mb-2">Attributes</h4>
//                         <div className="flex flex-wrap gap-2">
//                           {productDetails.attributes.map((attr, index) => (
//                             <span key={index} className="bg-gray-100 px-3 py-1 rounded-full text-sm">
//                               {attr.value}
//                             </span>
//                           ))}
//                         </div>
//                       </div>
//                     )}
//                     {productDetails.tags && productDetails.tags.length > 0 && (
//                       <div>
//                         <h4 className="font-medium mb-2">Tags</h4>
//                         <div className="flex flex-wrap gap-2">
//                           {productDetails.tags.map((tag, index) => (
//                             <span key={index} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
//                               {tag}
//                             </span>
//                           ))}
//                         </div>
//                       </div>
//                     )}
//                   </div>
//                 </div> */}
//               </div>
//             )}
//           </div>
//         </div>
//       )} */}
//     </>
//   );
// };

// export default ProductsByCategory;

// import React, { useState, useEffect } from 'react';
// import axios from 'axios';
// import Cookies from 'js-cookie';
// import { useParams } from 'react-router-dom';
// import { AiOutlineHeart, AiFillHeart } from 'react-icons/ai';
// import Header from '../Header/Header';
// import { useDispatch } from 'react-redux';
// import { useNavigate } from 'react-router-dom';
// import { addToCart } from '../../redux/slices/cartSlice'; 

// const ProductsByCategory = () => {
//   const { categoryId } = useParams();
//   const [products, setProducts] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(null);
//   const [wishlist, setWishlist] = useState([]);
//   const [category, setCategory] = useState(null);
//   const [wishlistLoading, setWishlistLoading] = useState({});
//   const dispatch = useDispatch();
//   const navigate = useNavigate();

//   const token = Cookies.get("ecom_user_token");

//   // Fetch initial wishlist when component mounts
//   useEffect(() => {
//     const fetchWishlist = async () => {
//       try {
//         const response = await axios.get(
//           'https://seller-backend-29o9.onrender.com/api/buyer/get-wishlist',
//           {
//             headers: {
//               Authorization: `Bearer ${token}`
//             }
//           }
//         );
//         const productIds = response.data.wishlist.map(item => item.productId._id);
//         setWishlist(productIds);
//       } catch (err) {
//         console.error("Failed to fetch wishlist:", err);
//       }
//     };

//     if (token) {
//       fetchWishlist();
//     }
//   }, [token]);

//   const handleAddToCart = async (product) => {
//     if (product.stock <= 0) {
//       alert("This product is out of stock");
//       return;
//     }
    
//     const quantity = 1;
//     try {
//       const response = await axios.post(
//         'https://seller-backend-29o9.onrender.com/api/buyer/add-to-cart',
//         {
//           productId: product._id,
//           quantity: quantity
//         },
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             'Content-Type': 'application/json'
//           }
//         }
//       );
//       navigate('/cart');
//     } catch (error) {
//       console.error("Add to cart API failed:", error);
//       alert("Failed to add to cart. Please try again.");
//     }
//   };

//   const toggleWishlist = async (productId) => {
//     if (!token) {
//       alert("Please login to manage your wishlist");
//       navigate('/login');
//       return;
//     }

//     setWishlistLoading(prev => ({ ...prev, [productId]: true }));

//     try {
//       if (wishlist.includes(productId)) {
//         await axios.delete(
//           `https://seller-backend-29o9.onrender.com/api/buyer/remove-from-wishlist/${productId}`,
//           {
//             headers: {
//               Authorization: `Bearer ${token}`
//             }
//           }
//         );
//         setWishlist(wishlist.filter(id => id !== productId));
//       } else {
//         await axios.post(
//           'https://seller-backend-29o9.onrender.com/api/buyer/add-to-wishlist',
//           { productId },
//           {
//             headers: {
//               Authorization: `Bearer ${token}`,
//               'Content-Type': 'application/json'
//             }
//           }
//         );
//         setWishlist([...wishlist, productId]);
//       }
//     } catch (error) {
//       console.error("Wishlist operation failed:", error);
//       alert("Failed to update wishlist. Please try again.");
//     } finally {
//       setWishlistLoading(prev => ({ ...prev, [productId]: false }));
//     }
//   };

//   useEffect(() => {
//     const fetchProducts = async () => {
//       if (!categoryId) return;

//       setLoading(true);
//       try {
//         const response = await axios.get(
//           `${process.env.REACT_APP_BACKEND_URL}/api/seller/product/get-by-category/${categoryId}`,
//           {
//             headers: {
//               Authorization: `Bearer ${token}`
//             }
//           }
//         );
//         // Filter out inactive products
//         const activeProducts = response.data.products.filter(
//           product => product.status === 'active'
//         );
//         setProducts(activeProducts || []);
//         setCategory(response?.data?.products[0]?.category?.name)
//       } catch (err) {
//         setError(err.message || "Something went wrong.");
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchProducts();
//   }, [categoryId, token]);

//   if (loading) {
//     return (
//       <div className="flex justify-center items-center h-screen">
//         <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="flex justify-center items-center h-screen">
//         <div className="text-red-500 text-lg">Error: {error}</div>
//       </div>
//     );
//   }

//   return (
//     <>
//       <Header/>
//       <div className="container mx-auto px-4 py-8">
//         <h1 className="text-3xl font-bold text-gray-800 mb-8 text-center">{category ? `${category}` : 'Our Products'}</h1>

//         <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
//           {products.map((product) => (
//             <div 
//               key={product._id} 
//               className={`bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 ${
//                 product.stock <= 0 ? 'opacity-60' : ''
//               }`}
//             >
//               <div className="relative">
//                 <img 
//                   src={product.images[0]?.blob || 'https://via.placeholder.com/300'} 
//                   alt={product.name} 
//                   className="w-full h-48 object-cover"
//                 />
//                 {product.stock <= 0 && (
//                   <div className="absolute top-0 left-0 w-full h-full bg-gray-100 bg-opacity-50 flex items-center justify-center">
//                     <span className="bg-white px-3 py-1 rounded-md text-sm font-medium text-gray-800">
//                       Out of Stock
//                     </span>
//                   </div>
//                 )}
//                 <button 
//                   onClick={() => toggleWishlist(product._id)}
//                   disabled={wishlistLoading[product._id] || product.stock <= 0}
//                   className={`absolute top-2 right-2 p-2 rounded-full transition-colors duration-200 ${
//                     product.stock <= 0 ? 'cursor-not-allowed' : ''
//                   }`}
//                 >
//                   {wishlistLoading[product._id] ? (
//                     <div className="h-6 w-6 flex items-center justify-center">
//                       <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500"></div>
//                     </div>
//                   ) : wishlist.includes(product._id) ? (
//                     <AiFillHeart className="text-red-500 h-6 w-6" />
//                   ) : (
//                     <AiOutlineHeart className="text-gray-600 h-6 w-6" />
//                   )}
//                 </button>
//               </div>
              
//               <div className="p-4">
//                 <h3 className="text-lg font-semibold text-gray-800 mb-1 truncate">{product?.name}</h3>
//                 <p className="text-gray-600 text-sm mb-2 line-clamp-2">{product?.description}</p>
                
//                 <div className="flex justify-between items-center mt-4">
//                   <span className="text-lg font-bold text-blue-600">
//                     ${product?.price?.toFixed(2) || '0.00'}
//                   </span>
//                   {product.stock > 0 ? (
//                     <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
//                       In Stock
//                     </span>
//                   ) : (
//                     <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">
//                       Out of Stock
//                     </span>
//                   )}
//                 </div>
                
//                 <button 
//                   className={`mt-4 w-full py-2 px-4 rounded-md transition-colors duration-200 ${
//                     product.stock > 0 
//                       ? 'bg-blue-600 hover:bg-blue-700 text-white' 
//                       : 'bg-gray-300 text-gray-500 cursor-not-allowed'
//                   }`}
//                   onClick={() => handleAddToCart(product)}
//                   disabled={product?.stock <= 0}
//                 >
//                   {product.stock > 0 ? 'Add to Cart' : 'Out of Stock'}
//                 </button>
//               </div>
//             </div>
//           ))}
//         </div>

//         {products.length === 0 && !loading && (
//           <div className="text-center py-12">
//             <p className="text-gray-500 text-lg">No active products found in this category.</p>
//           </div>
//         )}
//       </div>
//     </>
//   );
// };

// export default ProductsByCategory;

// import React, { useState, useEffect } from 'react';
// import axios from 'axios';
// import Cookies from 'js-cookie';
// import { useParams } from 'react-router-dom';
// import { AiOutlineHeart, AiFillHeart } from 'react-icons/ai';
// import Header from '../Header/Header';
// import { useDispatch } from 'react-redux';
// import { useNavigate } from 'react-router-dom';
// import { addToCart } from '../../redux/slices/cartSlice'; 

// const ProductsByCategory = () => {
//   const { categoryId } = useParams();
//   const [products, setProducts] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(null);
//   const [wishlist, setWishlist] = useState([]);
//   const [wishlistLoading, setWishlistLoading] = useState({}); // Track loading state per product
//   const dispatch = useDispatch();
//   const navigate = useNavigate();

//   const token = Cookies.get("ecom_user_token");

//   // Fetch initial wishlist when component mounts
//   useEffect(() => {
//     const fetchWishlist = async () => {
//       try {
//         const response = await axios.get(
//           'https://seller-backend-29o9.onrender.com/api/buyer/get-wishlist',
//           {
//             headers: {
//               Authorization: `Bearer ${token}`
//             }
//           }
//         );
//         // Extract product IDs from the wishlist items
//         const productIds = response.data.wishlist.map(item => item.productId._id);
//         setWishlist(productIds);
//       } catch (err) {
//         console.error("Failed to fetch wishlist:", err);
//       }
//     };

//     if (token) {
//       fetchWishlist();
//     }
//   }, [token]);

//   const handleAddToCart = async (product) => {
//     if (product.status !== 'active') {
//       alert("This product is currently unavailable");
//       return;
//     }
    
//     const quantity = 1;
//     try {
//       const response = await axios.post(
//         'https://seller-backend-29o9.onrender.com/api/buyer/add-to-cart',
//         {
//           productId: product._id,
//           quantity: quantity
//         },
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             'Content-Type': 'application/json'
//           }
//         }
//       );
//       navigate('/cart');
//     } catch (error) {
//       console.error("Add to cart API failed:", error);
//       alert("Failed to add to cart. Please try again.");
//     }
//   };

//   const toggleWishlist = async (productId) => {
//     if (!token) {
//       alert("Please login to manage your wishlist");
//       navigate('/login');
//       return;
//     }

//     setWishlistLoading(prev => ({ ...prev, [productId]: true }));

//     try {
//       if (wishlist.includes(productId)) {
//         // Remove from wishlist
//         await axios.delete(
//           `https://seller-backend-29o9.onrender.com/api/buyer/remove-from-wishlist/${productId}`,
//           {
//             headers: {
//               Authorization: `Bearer ${token}`
//             }
//           }
//         );
//         setWishlist(wishlist.filter(id => id !== productId));
//       } else {
//         // Add to wishlist
//         await axios.post(
//           'https://seller-backend-29o9.onrender.com/api/buyer/add-to-wishlist',
//           { productId },
//           {
//             headers: {
//               Authorization: `Bearer ${token}`,
//               'Content-Type': 'application/json'
//             }
//           }
//         );
//         setWishlist([...wishlist, productId]);
//       }
//     } catch (error) {
//       console.error("Wishlist operation failed:", error);
//       alert("Failed to update wishlist. Please try again.");
//       // Don't update the UI state if the API call fails
//     } finally {
//       setWishlistLoading(prev => ({ ...prev, [productId]: false }));
//     }
//   };

//   useEffect(() => {
//     const fetchProducts = async () => {
//       if (!categoryId) return;

//       setLoading(true);
//       try {
//         const response = await axios.get(
//           `${process.env.REACT_APP_BACKEND_URL}/api/seller/product/get-by-category/${categoryId}`,
//           {
//             headers: {
//               Authorization: `Bearer ${token}`
//             }
//           }
//         );
//         setProducts(response.data.products || []);
//       } catch (err) {
//         setError(err.message || "Something went wrong.");
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchProducts();
//   }, [categoryId, token]);

//   if (loading) {
//     return (
//       <div className="flex justify-center items-center h-screen">
//         <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="flex justify-center items-center h-screen">
//         <div className="text-red-500 text-lg">Error: {error}</div>
//       </div>
//     );
//   }

//   return (
//     <>
//       <Header/>
//       <div className="container mx-auto px-4 py-8">
//         <h1 className="text-3xl font-bold text-gray-800 mb-8 text-center">Our Products</h1>

//         <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
//           {products.map((product) => (
//             <div 
//               key={product._id} 
//               className={`bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 ${
//                 product.status !== 'active' ? 'opacity-60' : ''
//               }`}
//             >
//               <div className="relative">
//                 <img 
//                   src={product.images[0]?.blob || 'https://via.placeholder.com/300'} 
//                   alt={product.name} 
//                   className="w-full h-48 object-cover"
//                 />
//                 {product.status !== 'active' && (
//                   <div className="absolute top-0 left-0 w-full h-full bg-gray-100 bg-opacity-50 flex items-center justify-center">
//                     <span className="bg-white px-3 py-1 rounded-md text-sm font-medium text-gray-800">
//                       Currently Unavailable
//                     </span>
//                   </div>
//                 )}
//                 <button 
//                   onClick={() => toggleWishlist(product._id)}
//                   disabled={wishlistLoading[product._id] || product.status !== 'active'}
//                   className={`absolute top-2 right-2 p-2 rounded-full transition-colors duration-200 ${
//                     product.status !== 'active' ? 'cursor-not-allowed' : ''
//                   }`}
//                 >
//                   {wishlistLoading[product._id] ? (
//                     <div className="h-6 w-6 flex items-center justify-center">
//                       <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500"></div>
//                     </div>
//                   ) : wishlist.includes(product._id) ? (
//                     <AiFillHeart className="text-red-500 h-6 w-6" />
//                   ) : (
//                     <AiOutlineHeart className="text-gray-600 h-6 w-6" />
//                   )}
//                 </button>
//               </div>
              
//               <div className="p-4">
//                 <h3 className="text-lg font-semibold text-gray-800 mb-1 truncate">{product?.name}</h3>
//                 <p className="text-gray-600 text-sm mb-2 line-clamp-2">{product?.description}</p>
                
//                 <div className="flex justify-between items-center mt-4">
//                   <span className="text-lg font-bold text-blue-600">
//                     ${product?.price?.toFixed(2) || '0.00'}
//                   </span>
//                   {product.stock > 0 ? (
//                     <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
//                       In Stock
//                     </span>
//                   ) : (
//                     <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">
//                       Out of Stock
//                     </span>
//                   )}
//                 </div>
                
//                 <button 
//                   className={`mt-4 w-full py-2 px-4 rounded-md transition-colors duration-200 ${
//                     product.status === 'active' 
//                       ? 'bg-blue-600 hover:bg-blue-700 text-white' 
//                       : 'bg-gray-300 text-gray-500 cursor-not-allowed'
//                   }`}
//                   onClick={() => handleAddToCart(product)}
//                   disabled={product.status !== 'active'}
//                 >
//                   {product.status === 'active' ? 'Add to Cart' : 'Unavailable'}
//                 </button>
//               </div>
//             </div>
//           ))}
//         </div>

//         {products.length === 0 && !loading && (
//           <div className="text-center py-12">
//             <p className="text-gray-500 text-lg">No products found.</p>
//           </div>
//         )}
//       </div>
//     </>
//   );
// };

// export default ProductsByCategory;

// import React, { useState, useEffect } from 'react';
// import axios from 'axios';
// import Cookies from 'js-cookie';
// import { useParams } from 'react-router-dom';
// import { AiOutlineHeart, AiFillHeart } from 'react-icons/ai';
// import Header from '../Header/Header';
// import { useDispatch } from 'react-redux';
// import { useNavigate } from 'react-router-dom';
// import { addToCart } from '../../redux/slices/cartSlice'; 

// const ProductsByCategory = () => {
//   const { categoryId } = useParams();
//   const [products, setProducts] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(null);
//   const [wishlist, setWishlist] = useState([]);
//   const [wishlistLoading, setWishlistLoading] = useState({}); // Track loading state per product
//   const dispatch = useDispatch();
//   const navigate = useNavigate();

//   const token = Cookies.get("ecom_user_token");

//   // Fetch initial wishlist when component mounts
//   useEffect(() => {
//     const fetchWishlist = async () => {
//       try {
//         const response = await axios.get(
//           'https://seller-backend-29o9.onrender.com/api/buyer/get-wishlist',
//           {
//             headers: {
//               Authorization: `Bearer ${token}`
//             }
//           }
//         );
//         // Extract product IDs from the wishlist items
//         const productIds = response.data.wishlist.map(item => item.productId._id);
//         setWishlist(productIds);
//       } catch (err) {
//         console.error("Failed to fetch wishlist:", err);
//       }
//     };

//     if (token) {
//       fetchWishlist();
//     }
//   }, [token]);

//   const handleAddToCart = async (product) => {
//     const quantity = 1;
//     try {
//       const response = await axios.post(
//         'https://seller-backend-29o9.onrender.com/api/buyer/add-to-cart',
//         {
//           productId: product._id,
//           quantity: quantity
//         },
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             'Content-Type': 'application/json'
//           }
//         }
//       );
//       navigate('/cart');
//     } catch (error) {
//       console.error("Add to cart API failed:", error);
//       alert("Failed to add to cart. Please try again.");
//     }
//   };

//   const toggleWishlist = async (productId) => {
//     if (!token) {
//       alert("Please login to manage your wishlist");
//       navigate('/login');
//       return;
//     }

//     setWishlistLoading(prev => ({ ...prev, [productId]: true }));

//     try {
//       if (wishlist.includes(productId)) {
//         // Remove from wishlist
//         await axios.delete(
//           `https://seller-backend-29o9.onrender.com/api/buyer/remove-from-wishlist/${productId}`,
//           {
//             headers: {
//               Authorization: `Bearer ${token}`
//             }
//           }
//         );
//         setWishlist(wishlist.filter(id => id !== productId));
//       } else {
//         // Add to wishlist
//         await axios.post(
//           'https://seller-backend-29o9.onrender.com/api/buyer/add-to-wishlist',
//           { productId },
//           {
//             headers: {
//               Authorization: `Bearer ${token}`,
//               'Content-Type': 'application/json'
//             }
//           }
//         );
//         setWishlist([...wishlist, productId]);
//       }
//     } catch (error) {
//       console.error("Wishlist operation failed:", error);
//       alert("Failed to update wishlist. Please try again.");
//       // Don't update the UI state if the API call fails
//     } finally {
//       setWishlistLoading(prev => ({ ...prev, [productId]: false }));
//     }
//   };

//   useEffect(() => {
//     const fetchProducts = async () => {
//       if (!categoryId) return;

//       setLoading(true);
//       try {
//         const response = await axios.get(
//           `${process.env.REACT_APP_BACKEND_URL}/api/seller/product/get-by-category/${categoryId}`,
//           {
//             headers: {
//               Authorization: `Bearer ${token}`
//             }
//           }
//         );
//         setProducts(response.data.products || []);
//       } catch (err) {
//         setError(err.message || "Something went wrong.");
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchProducts();
//   }, [categoryId, token]);

//   if (loading) {
//     return (
//       <div className="flex justify-center items-center h-screen">
//         <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="flex justify-center items-center h-screen">
//         <div className="text-red-500 text-lg">Error: {error}</div>
//       </div>
//     );
//   }

//   return (
//     <>
//       <Header/>
//       <div className="container mx-auto px-4 py-8">
//         <h1 className="text-3xl font-bold text-gray-800 mb-8 text-center">Our Products</h1>

//         <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
//           {products.map((product) => (
//             <div key={product._id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
//               <div className="relative">
//                 <img 
//                   src={product.images[0]?.blob || 'https://via.placeholder.com/300'} 
//                   alt={product.name} 
//                   className="w-full h-48 object-cover"
//                 />
//                 <button 
//                   onClick={() => toggleWishlist(product._id)}
//                   disabled={wishlistLoading[product._id]}
//                   className="absolute top-2 right-2 p-2 rounded-full transition-colors duration-200"
//                 >
//                   {wishlistLoading[product._id] ? (
//                     <div className="h-6 w-6 flex items-center justify-center">
//                       <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500"></div>
//                     </div>
//                   ) : wishlist.includes(product._id) ? (
//                     <AiFillHeart className="text-red-500 h-6 w-6" />
//                   ) : (
//                     <AiOutlineHeart className="text-gray-600 h-6 w-6" />
//                   )}
//                 </button>
//               </div>
              
//               <div className="p-4">
//                 <h3 className="text-lg font-semibold text-gray-800 mb-1 truncate">{product?.name}</h3>
//                 <p className="text-gray-600 text-sm mb-2 line-clamp-2">{product?.description}</p>
                
//                 <div className="flex justify-between items-center mt-4">
//                   <span className="text-lg font-bold text-blue-600">
//                     ${product?.price?.toFixed(2) || '0.00'}
//                   </span>
//                   {product.stock > 0 ? (
//                     <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
//                       In Stock
//                     </span>
//                   ) : (
//                     <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">
//                       Out of Stock
//                     </span>
//                   )}
//                 </div>
                
//                 <button 
//                   className="mt-4 w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md transition-colors duration-200" 
//                   onClick={() => handleAddToCart(product)}
//                 >
//                   Add to Cart
//                 </button>
//               </div>
//             </div>
//           ))}
//         </div>

//         {products.length === 0 && !loading && (
//           <div className="text-center py-12">
//             <p className="text-gray-500 text-lg">No products found.</p>
//           </div>
//         )}
//       </div>
//     </>
//   );
// };

// export default ProductsByCategory;

// import React, { useState, useEffect } from 'react';
// import axios from 'axios';
// import Cookies from 'js-cookie';
// import { useParams } from 'react-router-dom';
// import { AiOutlineHeart, AiFillHeart } from 'react-icons/ai';
// import Header from '../Header/Header';
// import { useDispatch } from 'react-redux';
// import { useNavigate } from 'react-router-dom';
// import { addToCart } from '../../redux/slices/cartSlice'; 

// const ProductsByCategory = () => {
//   const { categoryId } = useParams();
//   const [products, setProducts] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(null);
//   const [wishlist, setWishlist] = useState([]);
//   const [wishlistLoading, setWishlistLoading] = useState({}); // Track loading state per product
//   const dispatch = useDispatch();
//   const navigate = useNavigate();

//   const token = Cookies.get("ecom_user_token");

//   // Fetch initial wishlist when component mounts
//   useEffect(() => {
//     const fetchWishlist = async () => {
//       try {
//         const response = await axios.get(
//           `${process.env.REACT_APP_BACKEND_URL}/api/buyer/get-wishlist`,
//           {
//             headers: {
//               Authorization: `Bearer ${token}`
//             }
//           }
//         );
//         setWishlist(response.data.wishlist.map(item => item.productId));
//       } catch (err) {
//         console.error("Failed to fetch wishlist:", err);
//       }
//     };

//     if (token) {
//       fetchWishlist();
//     }
//   }, [token]);

//   const handleAddToCart = async (product) => {
//     const quantity = 1;
//     try {
//       const response = await axios.post(
//         'https://seller-backend-29o9.onrender.com/api/buyer/add-to-cart',
//         {
//           productId: product._id,
//           quantity: quantity
//         },
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             'Content-Type': 'application/json'
//           }
//         }
//       );
//       navigate('/cart');
//     } catch (error) {
//       console.error("Add to cart API failed:", error);
//       alert("Failed to add to cart. Please try again.");
//     }
//   };

//   const toggleWishlist = async (productId) => {
//     if (!token) {
//       alert("Please login to manage your wishlist");
//       navigate('/login');
//       return;
//     }

//     setWishlistLoading(prev => ({ ...prev, [productId]: true }));

//     try {
//       if (wishlist.includes(productId)) {
//         // Remove from wishlist
//         await axios.delete(
//           `https://seller-backend-29o9.onrender.com/api/buyer/remove-from-wishlist/${productId}`,
//           {
//             headers: {
//               Authorization: `Bearer ${token}`
//             }
//           }
//         );
//         setWishlist(wishlist.filter(id => id !== productId));
//       } else {
//         // Add to wishlist
//         await axios.post(
//           'https://seller-backend-29o9.onrender.com/api/buyer/add-to-wishlist',
//           { productId },
//           {
//             headers: {
//               Authorization: `Bearer ${token}`,
//               'Content-Type': 'application/json'
//             }
//           }
//         );
//         setWishlist([...wishlist, productId]);
//       }
//     } catch (error) {
//       console.error("Wishlist operation failed:", error);
//       alert("Failed to update wishlist. Please try again.");
//       // Don't update the UI state if the API call fails
//     } finally {
//       setWishlistLoading(prev => ({ ...prev, [productId]: false }));
//     }
//   };

//   useEffect(() => {
//     const fetchProducts = async () => {
//       if (!categoryId) return;

//       setLoading(true);
//       try {
//         const response = await axios.get(
//           `${process.env.REACT_APP_BACKEND_URL}/api/seller/product/get-by-category/${categoryId}`,
//           {
//             headers: {
//               Authorization: `Bearer ${token}`
//             }
//           }
//         );
//         setProducts(response.data.products || []);
//       } catch (err) {
//         setError(err.message || "Something went wrong.");
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchProducts();
//   }, [categoryId, token]);

//   if (loading) {
//     return (
//       <div className="flex justify-center items-center h-screen">
//         <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="flex justify-center items-center h-screen">
//         <div className="text-red-500 text-lg">Error: {error}</div>
//       </div>
//     );
//   }

//   return (
//     <>
//       <Header/>
//       <div className="container mx-auto px-4 py-8">
//         <h1 className="text-3xl font-bold text-gray-800 mb-8 text-center">Our Products</h1>

//         <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
//           {products.map((product) => (
//             <div key={product._id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
//               <div className="relative">
//                 <img 
//                   src={product.images[0]?.blob || 'https://via.placeholder.com/300'} 
//                   alt={product.name} 
//                   className="w-full h-48 object-cover"
//                 />
//                 <button 
//                   onClick={() => toggleWishlist(product._id)}
//                   disabled={wishlistLoading[product._id]}
//                   className="absolute top-2 right-2 p-2 rounded-full transition-colors duration-200"
//                 >
//                   {wishlistLoading[product._id] ? (
//                     <div className="h-6 w-6 flex items-center justify-center">
//                       <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500"></div>
//                     </div>
//                   ) : wishlist.includes(product._id) ? (
//                     <AiFillHeart className="text-red-500 h-6 w-6" />
//                   ) : (
//                     <AiOutlineHeart className="text-gray-600 h-6 w-6" />
//                   )}
//                 </button>
//               </div>
              
//               <div className="p-4">
//                 <h3 className="text-lg font-semibold text-gray-800 mb-1 truncate">{product?.name}</h3>
//                 <p className="text-gray-600 text-sm mb-2 line-clamp-2">{product?.description}</p>
                
//                 <div className="flex justify-between items-center mt-4">
//                   <span className="text-lg font-bold text-blue-600">
//                     ${product?.price?.toFixed(2) || '0.00'}
//                   </span>
//                   {product.stock > 0 ? (
//                     <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
//                       In Stock
//                     </span>
//                   ) : (
//                     <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">
//                       Out of Stock
//                     </span>
//                   )}
//                 </div>
                
//                 <button 
//                   className="mt-4 w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md transition-colors duration-200" 
//                   onClick={() => handleAddToCart(product)}
//                 >
//                   Add to Cart
//                 </button>
//               </div>
//             </div>
//           ))}
//         </div>

//         {products.length === 0 && !loading && (
//           <div className="text-center py-12">
//             <p className="text-gray-500 text-lg">No products found.</p>
//           </div>
//         )}
//       </div>
//     </>
//   );
// };

// export default ProductsByCategory;

// import React, { useState, useEffect } from 'react';
// import axios from 'axios';
// import Cookies from 'js-cookie';
// import { useParams } from 'react-router-dom';
// import { AiOutlineHeart, AiFillHeart } from 'react-icons/ai';
// import Header from '../Header/Header';
// import { useDispatch } from 'react-redux';
// import { useNavigate } from 'react-router-dom';
// import { addToCart } from '../../redux/slices/cartSlice'; 

// const ProductsByCategory = () => {
//   const { categoryId } = useParams();
//   const [products, setProducts] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(null);
//   const [wishlist, setWishlist] = useState([]);
//   const dispatch = useDispatch();
//   const navigate = useNavigate();

//   const token = Cookies.get("ecom_user_token");

//   console.log("Component mounted");
//   console.log("Category ID from URL:", categoryId);
//   console.log("Token from cookies:", token);
//   console.log("Backend URL:", process.env.REACT_APP_BACKEND_URL);
//   const handleAddToCart = async (product) => {
//   const quantity = 1; // or any other default quantity

//   try {
//     const response = await axios.post(
//       'https://seller-backend-29o9.onrender.com/api/buyer/add-to-cart',
//       {
//         productId: product._id,
//         quantity: quantity
//       },
//       {
//         headers: {
//           Authorization: `Bearer ${token}`,
//           'Content-Type': 'application/json'
//         }
//       }
//     );

//     console.log("Backend add-to-cart response:", response.data);

//     // // After success, update Redux and navigate
//     // dispatch(addToCart({
//     //   id: product._id,
//     //   name: product?.name,
//     //   price: product?.price,
//     //   image: product?.images[0]?.blob || 'https://via.placeholder.com/300',
//     //   quantity: quantity
//     // }));

//     navigate('/cart');
//   } catch (error) {
//     console.error("Add to cart API failed:", error);
//     alert("Failed to add to cart. Please try again.");
//   }
// };


//   useEffect(() => {
//     const fetchProducts = async () => {
//       if (!categoryId) {
//         console.warn("Category ID is undefined. Skipping fetch.");
//         return;
//       }

//       console.log(`Fetching products for category ID: ${categoryId}`);
//       setLoading(true);

//       try {
//         const response = await axios.get(
//           `${process.env.REACT_APP_BACKEND_URL}/api/seller/product/get-by-category/${categoryId}`,
//           {
//             headers: {
//               Authorization: `Bearer ${token}`
//             }
//           }
//         );
//         console.log("API response:", response.data);

//         setProducts(response.data.products || []);
//       } catch (err) {
//         console.error("API error occurred:", err);
//         setError(err.message || "Something went wrong.");
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchProducts();
//   }, [categoryId, token]);

//   const toggleWishlist = (productId) => {
//     console.log(`Toggling wishlist for product ID: ${productId}`);
//     if (wishlist.includes(productId)) {
//       setWishlist(wishlist.filter(id => id !== productId));
//     } else {
//       setWishlist([...wishlist, productId]);
//     }
//   };

//   if (loading) {
//     return (
//       <div className="flex justify-center items-center h-screen">
//         <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="flex justify-center items-center h-screen">
//         <div className="text-red-500 text-lg">Error: {error}</div>
//       </div>
//     );
//   }

//   return (
//     <>
//     <Header/>
//     <div className="container mx-auto px-4 py-8">
        
//       <h1 className="text-3xl font-bold text-gray-800 mb-8 text-center">Our Products</h1>

//       <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
//         {products.map((product) => (
//           <div key={product._id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
//             <div className="relative">
//               <img 
//                 src={product.images[0].blob || 'https://via.placeholder.com/300'} 
//                 alt={product.name} 
//                 className="w-full h-48 object-cover"
//               />
//               <button 
//                 onClick={() => toggleWishlist(product._id)}
//                 className="absolute top-2 right-2 p-2 rounded-full transition-colors duration-200"
//               >
//                 {wishlist.includes(product._id) ? (
//                   <AiFillHeart className="text-red-500 h-6 w-6" />
//                 ) : (
//                   <AiOutlineHeart className="text-gray-600 h-6 w-6" />
//                 )}
//               </button>
//             </div>
            
//             <div className="p-4">
//               <h3 className="text-lg font-semibold text-gray-800 mb-1 truncate">{product?.name}</h3>
//               <p className="text-gray-600 text-sm mb-2 line-clamp-2">{product?.description}</p>
              
//               <div className="flex justify-between items-center mt-4">
//                 <span className="text-lg font-bold text-blue-600">
//                   ${product?.price?.toFixed(2) || '0.00'}
//                 </span>
//                 {product.stock > 0 ? (
//                   <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
//                     In Stock
//                   </span>
//                 ) : (
//                   <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">
//                     Out of Stock
//                   </span>
//                 )}
//               </div>
              
//               <button className="mt-4 w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md transition-colors duration-200" onClick={() => handleAddToCart(product)}>
//                 Add to Cart
//               </button>
//             </div>
//           </div>
//         ))}
//       </div>

//       {products.length === 0 && !loading && (
//         <div className="text-center py-12">
//           <p className="text-gray-500 text-lg">No products found.</p>
//         </div>
//       )}
//     </div>
//     </>
//   );
// };

// export default ProductsByCategory;


// import React, { useState, useEffect } from 'react';
// import axios from 'axios';
// import Cookies from 'js-cookie';
// import { useParams } from 'react-router-dom';
// import { AiOutlineHeart, AiFillHeart } from 'react-icons/ai';

// const ProductsByCategory = () => {
//   const { categoryId } = useParams();
//   const [products, setProducts] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(null);
//   const [wishlist, setWishlist] = useState([]);

//   const token = Cookies.get("ecom_user_token");

//   useEffect(() => {
//     const fetchProducts = async () => {
//       try {
//         const response = await axios.get(
//           `${process.env.REACT_APP_BACKEND_URL}/api/seller/product/get-by-category/${categoryId}`,
//           {
//             headers: {
//               Authorization: `Bearer ${token}`
//             }
//           }
//         );
//         setProducts(response.data.products || []);
//         setLoading(false);
//       } catch (err) {
//         setError(err.message);
//         setLoading(false);
//       }
//     };

//     if (categoryId) {
//       fetchProducts();
//     }
//   }, [categoryId, token]);

//   const toggleWishlist = (productId) => {
//     if (wishlist.includes(productId)) {
//       setWishlist(wishlist.filter(id => id !== productId));
//     } else {
//       setWishlist([...wishlist, productId]);
//     }
//   };

//   if (loading) {
//     return (
//       <div className="flex justify-center items-center h-screen">
//         <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="flex justify-center items-center h-screen">
//         <div className="text-red-500 text-lg">Error: {error}</div>
//       </div>
//     );
//   }

//   return (
//     <div className="container mx-auto px-4 py-8">
//       <h1 className="text-3xl font-bold text-gray-800 mb-8 text-center">Our Products</h1>
      
//       <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
//         {products.map((product) => (
//           <div key={product._id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
//             <div className="relative">
//               <img 
//                 src={product.images[0]?.url || 'https://via.placeholder.com/300'} 
//                 alt={product.name} 
//                 className="w-full h-48 object-cover"
//               />
//               <button 
//                 onClick={() => toggleWishlist(product._id)}
//                 className="absolute top-2 right-2 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors duration-200"
//               >
//                 {wishlist.includes(product._id) ? (
//                   <AiFillHeart className="text-red-500 h-6 w-6" />
//                 ) : (
//                   <AiOutlineHeart className="text-gray-600 h-6 w-6" />
//                 )}
//               </button>
//             </div>
            
//             <div className="p-4">
//               <h3 className="text-lg font-semibold text-gray-800 mb-1 truncate">{product.name}</h3>
//               <p className="text-gray-600 text-sm mb-2 line-clamp-2">{product.description}</p>
              
//               <div className="flex justify-between items-center mt-4">
//                 <span className="text-lg font-bold text-blue-600">
//                   ${product.price?.toFixed(2) || '0.00'}
//                 </span>
//                 {product.stock > 0 ? (
//                   <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
//                     In Stock
//                   </span>
//                 ) : (
//                   <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">
//                     Out of Stock
//                   </span>
//                 )}
//               </div>
              
//               <button className="mt-4 w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md transition-colors duration-200">
//                 Add to Cart
//               </button>
//             </div>
//           </div>
//         ))}
//       </div>

//       {products.length === 0 && (
//         <div className="text-center py-12">
//           <p className="text-gray-500 text-lg">No products found.</p>
//         </div>
//       )}
//     </div>
//   );
// };

// export default ProductsByCategory;

